import React, { useEffect, useMemo, useState } from 'react';
import {
  Container,
  Table,
  Button,
  Form,
  InputGroup,
  Card,
  Modal,
  Spinner,
  Dropdown,
  OverlayTrigger,
  Popover,
  Tooltip,
  Offcanvas,
  Tabs,
  Tab,
  Alert,
  Badge,
  ListGroup,
} from 'react-bootstrap';
import {
  useGetMeetingsQuery,
  useCreateMeetingMutation,
  useUpdateMeetingMutation,
  useDeleteMeetingMutation,
  useGetLeadsQuery,
  useCreateLeadMutation,
  useUpdateLeadMutation,
  useGetClientsQuery,
  useUpdateClientMutation,
  useGetContactsQuery,
  useUpdateContactMutation,
  useGetAccountsQuery,
  useCreateAccountMutation,
  useUpdateAccountMutation,
  useGetDealsQuery,
  useGetEmailsQuery,
  useGetRulesEngineConfigQuery,
  useGetUserPreferencesQuery,
} from '../services/api';
import { DEFAULT_RULES_ENGINE_CONFIG, normalizeRulesEngineConfig } from '../constants/rulesEngine';
import { useUser } from '../contexts/UserContext';
import { useUserPreferencesQueryArg } from '../hooks/useTenantIdForPreferences';
import { buildEmailThreadsForEntity, mergeEmailResponses } from '../utils/crmEmailThreads';
import {
  getDateKeyInTimezone,
  getTenantTimezoneFromPreferencesData,
  toLocaleDateStringInTimezone,
  toLocaleTimeStringInTimezone,
} from '../utils/tenantTimezone';
import { mergeLabelCatalogCandidates } from '../utils/crmLabels';
import {
  formatHtmlDateValueToUsStorage,
} from '../utils/crmContractRenewal';
import {
  Search,
  Plus,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Phone,
  People,
  Envelope,
  Clock,
  Flag,
  ChatDots,
  Briefcase,
  Person,
  Building,
  FileText,
  Calendar,
  Dot,
  CheckSquare,
  BuildingFill,
  GeoAltFill,
  Globe2,
  Linkedin,
  PeopleFill,
  CupStraw,
  Camera,
  Tag,
  InfoCircle,
  Sticky,
  CurrencyDollar,
  ListUl,
  Calendar3,
  Trash,
  Check,
  Star,
  StarFill,
  Funnel,
  ArrowRight,
  X,
  XLg,
  Diamond,
  DiamondFill,
} from 'react-bootstrap-icons';

interface Meeting {
  id: string;
  title: string;
  startTime: string;
  endTime?: string;
  meetingType?: string;
  accountId?: string;
  clientId?: string;
  contactId?: string;
  dealId?: string;
  leadId?: string;
  createdAt?: string;
  description?: string;
  location?: string;
  meetingUrl?: string;
  notes?: string;
  tags?: string[];
  visibility?: string;
  owner?: string;
  purposeTypeId?: string;
  priority?: 'low' | 'normal' | 'high';
  freeBusy?: 'free' | 'busy';
  markAsDone?: boolean;
  organizationName?: string;
  contactName?: string;
  contactEmail?: string;
  dealLeadLabel?: string;
  phone?: string;
  value?: string;
  expectedCloseDate?: string;
  businessUnit?: string;
  inStore?: boolean;
  sourceOrigin?: string;
  sourceChannel?: string;
  sourceChannelId?: string;
  contactStage?: string;
  accountStage?: string;
  address?: string;
  website?: string;
  linkedInProfile?: string;
  numberOfEmployees?: string;
  annualRevenue?: string;
  industry?: string;
  workPhone?: string;
  cellPhone?: string;
  assistantName?: string;
  assistantPhone?: string;
  assistantEmail?: string;
  decisionMaker?: boolean;
}

type DateFilter = 'all' | 'todo' | 'overdue' | 'today' | 'tomorrow' | 'this-week' | 'next-week' | 'done';
type ActivityTypeId = 'call' | 'meeting' | 'text' | 'task' | 'deadline' | 'email' | 'lunch';
type ActivityViewMode = 'list' | 'calendar';
type OwnerFilterPanelTab = 'favorites' | 'owners' | 'filters';

interface ActivityTypeOption {
  id: ActivityTypeId;
  label: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  meetingType: string;
}

interface ActivityPurposeOption {
  id: string;
  name: string;
}

type ActivityVisibilityFilterId = 'all' | 'global' | 'personal';

type ActivityVisibilityFilterOption = {
  id: ActivityVisibilityFilterId;
  label: string;
  description: string;
};

interface ActivityFormData {
  title: string;
  description: string;
  startTime: string;
  endTime: string;
  location: string;
  meetingType: string;
  meetingUrl: string;
  dealId: string;
  leadId: string;
  contactId: string;
  clientId: string;
  accountId: string;
  organizationName: string;
  contactName: string;
  contactEmail: string;
  dealLeadLabel: string;
  purposeTypeId: string;
  notes: string;
  priority: 'low' | 'normal' | 'high';
  freeBusy: 'free' | 'busy';
  videoCallProvider: '' | 'zoom' | 'teams' | 'google-meet';
  markAsDone: boolean;
}

type ActivityEditableFieldId =
  | 'name'
  | 'email'
  | 'phone'
  | 'priority'
  | 'value'
  | 'contactStage'
  | 'expectedCloseDate'
  | 'businessUnit'
  | 'sourceOrigin'
  | 'sourceChannel'
  | 'sourceChannelId'
  | 'owner'
  | 'visibility'
  | 'organization'
  | 'accountStage'
  | 'address'
  | 'website'
  | 'linkedInProfile'
  | 'numberOfEmployees'
  | 'annualRevenue'
  | 'industry'
  | 'title'
  | 'workPhone'
  | 'cellPhone'
  | 'assistantName'
  | 'assistantPhone'
  | 'assistantEmail';

type ActivityEditableFieldConfig = {
  inputType: 'text' | 'date';
  placeholder: string;
};

const ACTIVITY_EDITABLE_FIELD_CONFIG: Record<ActivityEditableFieldId, ActivityEditableFieldConfig> = {
  name: { inputType: 'text', placeholder: 'No name' },
  email: { inputType: 'text', placeholder: 'No email' },
  phone: { inputType: 'text', placeholder: 'No phone' },
  priority: { inputType: 'text', placeholder: 'Priority' },
  value: { inputType: 'text', placeholder: '$ Value' },
  contactStage: { inputType: 'text', placeholder: 'Contact Stage' },
  expectedCloseDate: { inputType: 'date', placeholder: 'Expected close date' },
  businessUnit: { inputType: 'text', placeholder: 'Business Unit' },
  sourceOrigin: { inputType: 'text', placeholder: 'Source origin' },
  sourceChannel: { inputType: 'text', placeholder: 'Source channel' },
  sourceChannelId: { inputType: 'text', placeholder: 'Source channel ID' },
  owner: { inputType: 'text', placeholder: 'Owner' },
  visibility: { inputType: 'text', placeholder: 'Visibility' },
  organization: { inputType: 'text', placeholder: 'No organization' },
  accountStage: { inputType: 'text', placeholder: 'Account Stage' },
  address: { inputType: 'text', placeholder: 'Address' },
  website: { inputType: 'text', placeholder: 'Website' },
  linkedInProfile: { inputType: 'text', placeholder: 'LinkedIn profile' },
  numberOfEmployees: { inputType: 'text', placeholder: 'Number of employees' },
  annualRevenue: { inputType: 'text', placeholder: 'Annual revenue' },
  industry: { inputType: 'text', placeholder: 'Industry' },
  title: { inputType: 'text', placeholder: 'Title' },
  workPhone: { inputType: 'text', placeholder: 'Work phone' },
  cellPhone: { inputType: 'text', placeholder: 'Cell phone' },
  assistantName: { inputType: 'text', placeholder: "Assistant's Name" },
  assistantPhone: { inputType: 'text', placeholder: "Assistant's Phone" },
  assistantEmail: { inputType: 'text', placeholder: "Assistant's Email" },
};

type DealLeadOption = {
  id: string;
  type: 'deal' | 'lead';
  label: string;
};

type OrganizationOption = {
  id: string;
  type: 'account' | 'contactOrg' | 'leadOrg';
  label: string;
};

const MEETING_TYPE_LABELS: Record<string, string> = {
  phone: 'Call',
  'in-person': 'Meeting',
  video: 'Meeting',
  email: 'Email',
};

const MEETING_TYPE_ICONS: Record<string, React.ComponentType<{ size?: number; className?: string }>> = {
  phone: Phone,
  'in-person': People,
  video: People,
  email: Envelope,
};

const ACTIVITY_PRIORITY_OPTIONS = [
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Medium' },
  { value: 'low', label: 'Low' },
] as const;
const ALL_PRIORITIES_FILTER = 'Priority';

const getActivityPriorityLabel = (priority?: string): string => {
  const normalized = String(priority || 'normal').trim().toLowerCase();
  return ACTIVITY_PRIORITY_OPTIONS.find((option) => option.value === normalized)?.label || 'Medium';
};

const getActivityPriorityVariant = (priority?: string): string => {
  const normalized = String(priority || 'normal').trim().toLowerCase();
  if (normalized === 'high') return 'danger';
  if (normalized === 'low') return 'secondary';
  return 'warning';
};

const SOURCE_ORIGIN_OPTIONS = [
  'Manually created',
  'Prospector',
  'Lead Suggestions',
  'Campaigns',
  'Web forms',
  'Chatbot',
  'Live chat',
  'Web visitors',
  'Marketplace',
  'Messaging Inbox',
] as const;

const ACTIVITY_TYPE_OPTIONS: ActivityTypeOption[] = [
  { id: 'call', label: 'Call', icon: Phone, meetingType: 'phone' },
  { id: 'meeting', label: 'Meeting', icon: People, meetingType: 'in-person' },
  { id: 'text', label: 'Text', icon: ChatDots, meetingType: 'phone' },
  { id: 'task', label: 'Task', icon: Clock, meetingType: 'in-person' },
  { id: 'deadline', label: 'Deadline', icon: Flag, meetingType: 'in-person' },
  { id: 'email', label: 'Email', icon: Envelope, meetingType: 'email' },
  { id: 'lunch', label: 'Lunch', icon: CupStraw, meetingType: 'in-person' },
];

const DEFAULT_ACTIVITY_PURPOSE_OPTIONS: ActivityPurposeOption[] = [
  { id: 'internal-sales-meeting', name: 'Internal Sales Meeting' },
  { id: 'internal-developer-meetings', name: 'Internal Developer Meetings' },
  { id: 'client-sales-meetings', name: 'Client Sales Meetings' },
];

const DEFAULT_ACTIVITY_PURPOSE_ID = 'internal-sales-meeting';
const DEFAULT_OWNER_FILTER_LABEL = 'Everyone';
const DEFAULT_SELF_OWNER_LABEL = 'PhiLL Kross';
const ALL_LABELS_FILTER = 'All labels';

const getSharedLabelColors = (): Record<string, string> => {
  const defaultColors: Record<string, string> = {
    HOT: 'danger',
    COLD: 'primary',
    WARM: 'warning',
    'QUARTER 2': 'secondary',
    'QUARTER 1': 'info',
    'QUARTER 3': 'info',
    'QUARTER 4': 'warning',
    NTTM: 'info',
    FRANCHISE: 'success',
    '1-10 LOCATIONS': 'warning',
    CHAINS: 'success',
    'I.P.M.': 'secondary',
  };

  try {
    const stored = localStorage.getItem('labelColors');
    if (stored) {
      const storedColors = JSON.parse(stored);
      return { ...defaultColors, ...storedColors };
    }
  } catch {
    // Ignore storage parsing issues and fall back to defaults.
  }

  return defaultColors;
};
const ACTIVITY_OWNER_FAVORITES_STORAGE_KEY = 'activityOwnerFavorites';
const ACTIVITY_FILTER_FAVORITES_STORAGE_KEY = 'activityFilterFavorites';

const ACTIVITY_VISIBILITY_FILTER_OPTIONS: ActivityVisibilityFilterOption[] = [
  {
    id: 'all',
    label: 'All activities',
    description: 'Show every activity',
  },
  {
    id: 'global',
    label: 'Global (everyone)',
    description: 'Only global visibility activities',
  },
  {
    id: 'personal',
    label: 'Personal (private)',
    description: 'Only personal visibility activities',
  },
];

const normalizeOwnerKey = (value: string): string =>
  String(value || '')
    .replace(/\(\s*you\s*\)/gi, '')
    .trim()
    .toLowerCase();

const toLocalDateTimeInputValue = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const getActivityTypeOption = (typeId: ActivityTypeId): ActivityTypeOption =>
  ACTIVITY_TYPE_OPTIONS.find((option) => option.id === typeId) || ACTIVITY_TYPE_OPTIONS[0];

const createDefaultActivityForm = (typeId: ActivityTypeId): ActivityFormData => {
  const now = new Date();
  const end = new Date(now.getTime() + 60 * 60 * 1000);
  const activityType = getActivityTypeOption(typeId);
  return {
    title: activityType.label,
    description: '',
    startTime: toLocalDateTimeInputValue(now),
    endTime: toLocalDateTimeInputValue(end),
    location: '',
    meetingType: activityType.meetingType,
    meetingUrl: '',
    dealId: '',
    leadId: '',
    contactId: '',
    clientId: '',
    accountId: '',
    organizationName: '',
    contactName: '',
    contactEmail: '',
    dealLeadLabel: '',
    purposeTypeId: DEFAULT_ACTIVITY_PURPOSE_ID,
    notes: '',
    priority: 'normal',
    freeBusy: 'free',
    videoCallProvider: '',
    markAsDone: false,
  };
};

const deriveActivityTypeFromMeeting = (meeting: Meeting): ActivityTypeId => {
  const tags = Array.isArray(meeting.tags) ? meeting.tags : [];
  const activityTag = tags.find((t) => String(t).startsWith('activity:'));
  if (activityTag) {
    const id = String(activityTag).slice('activity:'.length) as ActivityTypeId;
    if (ACTIVITY_TYPE_OPTIONS.some((o) => o.id === id)) return id;
  }
  const mt = String(meeting.meetingType || '').toLowerCase();
  if (mt === 'email') return 'email';
  if (mt === 'video' || mt === 'in-person') return 'meeting';
  return 'call';
};

const derivePurposeIdFromMeeting = (meeting: Meeting): string => {
  const tags = Array.isArray(meeting.tags) ? meeting.tags : [];
  const purposeTag = tags.find((t) => String(t).startsWith('purpose:'));
  if (purposeTag) {
    const id = String(purposeTag).slice('purpose:'.length);
    if (DEFAULT_ACTIVITY_PURPOSE_OPTIONS.some((o) => o.id === id)) return id;
  }
  return DEFAULT_ACTIVITY_PURPOSE_ID;
};

const decodeTagValue = (value: string): string => {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
};

const getMeetingTagValue = (meeting: Meeting, prefix: string): string => {
  const tags = Array.isArray(meeting.tags) ? meeting.tags : [];
  const match = tags.find((tag) => String(tag).startsWith(prefix));
  if (!match) return '';
  return decodeTagValue(String(match).slice(prefix.length));
};

const getMeetingPriorityValue = (meeting: Meeting | null | undefined): 'high' | 'normal' | 'low' => {
  if (!meeting) return 'normal';
  const normalized = String(
    meeting.priority
    || getMeetingTagValue(meeting, 'meta_priority:')
    || 'normal'
  ).trim().toLowerCase();
  if (normalized === 'high' || normalized === 'low') return normalized;
  return 'normal';
};

const getMeetingDoneValue = (meeting: Meeting | null | undefined): boolean => {
  if (!meeting) return false;
  const raw = String(meeting.markAsDone ?? getMeetingTagValue(meeting, 'meta_done:') ?? '').trim().toLowerCase();
  return raw === '1' || raw === 'true' || raw === 'yes';
};

const upsertMeetingMetaTag = (tags: string[], prefix: string, value: string): string[] => {
  const cleaned = tags.filter((tag) => !String(tag).startsWith(prefix));
  const trimmed = String(value || '').trim();
  if (!trimmed) return cleaned;
  return [...cleaned, `${prefix}${encodeURIComponent(trimmed)}`];
};

const META_TAG_PREFIX_BY_FIELD: Partial<Record<ActivityEditableFieldId, string>> = {
  contactStage: 'meta_contact_stage:',
  accountStage: 'meta_account_stage:',
  expectedCloseDate: 'meta_expected_close_date:',
  businessUnit: 'meta_business_unit:',
  sourceOrigin: 'meta_source_origin:',
  sourceChannel: 'meta_source_channel:',
  sourceChannelId: 'meta_source_channel_id:',
  phone: 'meta_phone:',
  value: 'meta_value:',
  address: 'meta_address:',
  website: 'meta_website:',
  linkedInProfile: 'meta_linkedin_profile:',
  numberOfEmployees: 'meta_number_of_employees:',
  annualRevenue: 'meta_annual_revenue:',
  industry: 'meta_industry:',
};

const meetingToActivityForm = (meeting: Meeting): { typeId: ActivityTypeId; form: ActivityFormData } => {
  const typeId = deriveActivityTypeFromMeeting(meeting);
  const option = getActivityTypeOption(typeId);
  const start = meeting.startTime ? new Date(meeting.startTime) : new Date();
  const end = meeting.endTime ? new Date(meeting.endTime) : new Date(start.getTime() + 3600000);
  const mt = String(meeting.meetingType || '').toLowerCase();
  const hasUrl = String(meeting.meetingUrl || '').trim().length > 0;
  const isVideo = mt === 'video' && hasUrl;
  let videoProvider: '' | 'zoom' | 'teams' | 'google-meet' = '';
  if (isVideo) {
    const url = String(meeting.meetingUrl || '').toLowerCase();
    if (url.includes('zoom')) videoProvider = 'zoom';
    else if (url.includes('teams.microsoft') || url.includes('teams.live')) videoProvider = 'teams';
    else if (url.includes('meet.google')) videoProvider = 'google-meet';
  }
  return {
    typeId,
    form: {
      title: meeting.title || option.label,
      description: String(meeting.description || ''),
      startTime: toLocalDateTimeInputValue(start),
      endTime: toLocalDateTimeInputValue(end),
      location: String(meeting.location || ''),
      meetingType: videoProvider ? 'video' : option.meetingType,
      meetingUrl: String(meeting.meetingUrl || ''),
      dealId: meeting.dealId ? String(meeting.dealId) : '',
      leadId: meeting.leadId ? String(meeting.leadId) : '',
      contactId: meeting.contactId ? String(meeting.contactId) : '',
      clientId: meeting.clientId ? String(meeting.clientId) : '',
      accountId: meeting.accountId ? String(meeting.accountId) : '',
      organizationName: String(meeting.organizationName || getMeetingTagValue(meeting, 'meta_org:') || ''),
      contactName: String(meeting.contactName || getMeetingTagValue(meeting, 'meta_contact_name:') || ''),
      contactEmail: String(meeting.contactEmail || getMeetingTagValue(meeting, 'meta_contact_email:') || ''),
      dealLeadLabel: String(meeting.dealLeadLabel || getMeetingTagValue(meeting, 'meta_deal_lead:') || ''),
      purposeTypeId: String(meeting.purposeTypeId || derivePurposeIdFromMeeting(meeting)),
      notes: String(meeting.notes || ''),
      priority: getMeetingPriorityValue(meeting),
      freeBusy: (meeting.freeBusy || getMeetingTagValue(meeting, 'meta_free_busy:')) === 'busy' ? 'busy' : 'free',
      videoCallProvider: videoProvider,
      markAsDone: Boolean(
        meeting.markAsDone
        ?? ['1', 'true', 'yes'].includes(getMeetingTagValue(meeting, 'meta_done:').toLowerCase())
      ),
    },
  };
};

const Activities: React.FC = () => {
  const { currentUser } = useUser();
  const [searchQuery, setSearchQuery] = useState('');
  const [showOwnerFilter, setShowOwnerFilter] = useState(false);
  const [ownerFilterTab, setOwnerFilterTab] = useState<OwnerFilterPanelTab>('owners');
  const [ownerFilterSearchQuery, setOwnerFilterSearchQuery] = useState('');
  const [hoveredOwnerRowKey, setHoveredOwnerRowKey] = useState<string | null>(null);
  const [selectedOwner, setSelectedOwner] = useState<string>(DEFAULT_OWNER_FILTER_LABEL);
  const [selectedPriority, setSelectedPriority] = useState<string>('');
  const [selectedTag, setSelectedTag] = useState<string>('');
  const [showPriorityFilter, setShowPriorityFilter] = useState(false);
  const [showTagsFilter, setShowTagsFilter] = useState(false);
  const [labelsFilterSearchQuery, setLabelsFilterSearchQuery] = useState('');
  const [customLabels, setCustomLabels] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('customLabels');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  });
  const [selectedVisibilityFilterId, setSelectedVisibilityFilterId] = useState<ActivityVisibilityFilterId>('all');
  const [favoriteOwnerKeys, setFavoriteOwnerKeys] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem(ACTIVITY_OWNER_FAVORITES_STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed.map((entry) => normalizeOwnerKey(String(entry || ''))).filter(Boolean) : [];
    } catch {
      return [];
    }
  });
  const [favoriteVisibilityFilterIds, setFavoriteVisibilityFilterIds] = useState<ActivityVisibilityFilterId[]>(() => {
    try {
      const stored = localStorage.getItem(ACTIVITY_FILTER_FAVORITES_STORAGE_KEY);
      const parsed = stored ? JSON.parse(stored) : [];
      if (!Array.isArray(parsed)) return [];
      const allowed = new Set<ActivityVisibilityFilterId>(['all', 'global', 'personal']);
      return parsed
        .map((entry) => String(entry) as ActivityVisibilityFilterId)
        .filter((entry) => allowed.has(entry));
    } catch {
      return [];
    }
  });
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [dateFilter, setDateFilter] = useState<DateFilter>('todo');
  // Bulk-action selection: stores activity ids the user has ticked in column 1.
  // Selection is intentionally NOT auto-cleared when filters change — the server
  // doesn't care about UI filter state and operators sometimes want to filter
  // one way to pick items, then change filters and continue picking.
  const [selectedRowIds, setSelectedRowIds] = useState<Set<string>>(new Set());
  const [isBulkUpdating, setIsBulkUpdating] = useState(false);
  const [activityViewMode, setActivityViewMode] = useState<ActivityViewMode>('list');
  const [calendarMonth, setCalendarMonth] = useState<Date>(() => {
    const current = new Date();
    return new Date(current.getFullYear(), current.getMonth(), 1, 12, 0, 0, 0);
  });

  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [editingMeetingId, setEditingMeetingId] = useState<string | null>(null);
  const [showActivitySidebar, setShowActivitySidebar] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState<Meeting | null>(null);
  const [showAllActivityDetailFields, setShowAllActivityDetailFields] = useState(false);
  const [showAllActivityPersonFields, setShowAllActivityPersonFields] = useState(false);
  const [showAllActivityOrganizationFields, setShowAllActivityOrganizationFields] = useState(false);
  const [isEditingContractRenewal, setIsEditingContractRenewal] = useState(false);
  const [contractRenewalDraft, setContractRenewalDraft] = useState('');
  const [activitySidebarTab, setActivitySidebarTab] = useState<'notes' | 'activity' | 'email' | 'files'>('notes');
  const [activitySidebarNoteDraft, setActivitySidebarNoteDraft] = useState('');
  const [activitySidebarHoveredField, setActivitySidebarHoveredField] = useState<ActivityEditableFieldId | null>(null);
  const [activitySidebarEditingFieldId, setActivitySidebarEditingFieldId] = useState<ActivityEditableFieldId | null>(null);
  const [activitySidebarFieldDraftValue, setActivitySidebarFieldDraftValue] = useState('');
  const [showActivityLabelsDropdown, setShowActivityLabelsDropdown] = useState(false);
  const [activityLabelSearchQuery, setActivityLabelSearchQuery] = useState('');
  const [isHoveringActivityLabels, setIsHoveringActivityLabels] = useState(false);
  const [selectedActivityType, setSelectedActivityType] = useState<ActivityTypeId>('call');
  const [activityForm, setActivityForm] = useState<ActivityFormData>(() => createDefaultActivityForm('call'));
  const [dealLeadSearch, setDealLeadSearch] = useState('');
  const [showDealLeadDropdown, setShowDealLeadDropdown] = useState(false);
  const [peopleSearch, setPeopleSearch] = useState('');
  const [showPeopleDropdown, setShowPeopleDropdown] = useState(false);
  const [orgSearch, setOrgSearch] = useState('');
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);

  const {
    data: meetingsData,
    isLoading: isLoadingMeetings,
    error: meetingsError,
    refetch: refetchMeetings,
  } = useGetMeetingsQuery({});
  const [createMeeting, { isLoading: isCreatingMeeting }] = useCreateMeetingMutation();
  const [updateMeeting, { isLoading: isUpdatingMeeting }] = useUpdateMeetingMutation();
  const [updateLead, { isLoading: isUpdatingLead }] = useUpdateLeadMutation();
  const [createLead] = useCreateLeadMutation();
  const [updateClient, { isLoading: isUpdatingClient }] = useUpdateClientMutation();
  const [updateContact, { isLoading: isUpdatingContact }] = useUpdateContactMutation();
  const [deleteMeeting, { isLoading: isDeletingMeetings }] = useDeleteMeetingMutation();
  const { data: rulesEngineConfigData } = useGetRulesEngineConfigQuery(undefined);
  const userPreferencesQueryArg = useUserPreferencesQueryArg();
  const { data: userPreferencesData } = useGetUserPreferencesQuery(userPreferencesQueryArg);
  const { data: leadsData, refetch: refetchLeads } = useGetLeadsQuery({});
  const { data: clientsData, refetch: refetchClients } = useGetClientsQuery({});
  const { data: contactsData, refetch: refetchContacts } = useGetContactsQuery({});
  const { data: accountsData, refetch: refetchAccounts } = useGetAccountsQuery({});
  const [createAccount] = useCreateAccountMutation();
  const [updateAccount] = useUpdateAccountMutation();
  const { data: dealsData } = useGetDealsQuery({});

  const tenantTimezone = useMemo(
    () => getTenantTimezoneFromPreferencesData(userPreferencesData),
    [userPreferencesData]
  );
  const rulesEngineConfig = useMemo(
    () => normalizeRulesEngineConfig(rulesEngineConfigData || DEFAULT_RULES_ENGINE_CONFIG),
    [rulesEngineConfigData]
  );
  const accountStageOptions = rulesEngineConfig.accountStages;
  const contactStageOptions = rulesEngineConfig.contactStages;

  const meetings = useMemo(() => (meetingsData || []) as Meeting[], [meetingsData]);
  const leads = useMemo(() => (leadsData || []) as {
    id: string;
    name?: string;
    organization?: string;
    email?: string;
    phone?: string;
    title?: string;
    value?: string;
    expectedCloseDate?: string;
    businessUnit?: string;
    inStore?: boolean;
    accountStage?: string;
    address?: string;
    website?: string;
    sourceChannelId?: string;
    linkedInProfile?: string;
    numberOfEmployees?: string;
    annualRevenue?: string;
    industry?: string;
    sourceOrigin?: string;
    sourceChannel?: string;
    contractRenewalDate?: string;
  }[], [leadsData]);
  const clients = useMemo(() => (clientsData || []) as {
    id: string;
    name?: string;
    organization?: string;
    phone?: string;
    email?: string;
    value?: string;
    expectedCloseDate?: string;
    businessUnit?: string;
    inStore?: boolean;
    accountStage?: string;
    contactStage?: string;
    address?: string;
    website?: string;
    sourceChannelId?: string;
    linkedInProfile?: string;
    numberOfEmployees?: string;
    annualRevenue?: string;
    industry?: string;
    sourceOrigin?: string;
    sourceChannel?: string;
    contractRenewalDate?: string;
  }[], [clientsData]);
  const accounts = useMemo(() => (accountsData || []) as {
    id: string;
    name?: string;
    contractRenewalDate?: string;
  }[], [accountsData]);
  const contacts = useMemo(() => (contactsData || []) as {
    id: string;
    firstName?: string;
    lastName?: string;
    organization?: string;
    email?: string;
    phone?: string;
    title?: string;
    value?: string;
    expectedCloseDate?: string;
    businessUnit?: string;
    inStore?: boolean;
    contactStage?: string;
    sourceOrigin?: string;
    sourceChannel?: string;
    sourceChannelId?: string;
    linkedInProfile?: string;
    numberOfEmployees?: string;
    annualRevenue?: string;
    address?: string;
    website?: string;
    industry?: string;
    accountStage?: string;
    clientId?: string;
  }[], [contactsData]);
  const deals = useMemo(() => (dealsData || []) as { id: string; name?: string }[], [dealsData]);

  const leadMap = useMemo(() => new Map(leads.map((lead) => [lead.id, lead])), [leads]);
  const clientMap = useMemo(() => new Map(clients.map((client) => [client.id, client])), [clients]);
  const contactMap = useMemo(() => new Map(contacts.map((contact) => [contact.id, contact])), [contacts]);
  const dealMap = useMemo(() => new Map(deals.map((deal) => [deal.id, deal])), [deals]);
  const accountMap = useMemo(() => new Map(accounts.map((account) => [account.id, account])), [accounts]);

  const selectedActivityContact = useMemo(
    () => (selectedMeeting?.contactId ? contactMap.get(selectedMeeting.contactId) : null),
    [selectedMeeting?.contactId, contactMap]
  );
  const selectedActivityClient = useMemo(
    () => (selectedMeeting?.clientId ? clientMap.get(selectedMeeting.clientId) : null),
    [selectedMeeting?.clientId, clientMap]
  );
  const selectedActivityLead = useMemo(
    () => (selectedMeeting?.leadId ? leadMap.get(selectedMeeting.leadId) : null),
    [selectedMeeting?.leadId, leadMap]
  );

  const activityEmailAddress = useMemo(() => {
    if (!selectedMeeting) return '';
    if (selectedMeeting.contactId) {
      return String(contactMap.get(selectedMeeting.contactId)?.email || '').trim().toLowerCase();
    }
    if (selectedMeeting.leadId) {
      return String(leadMap.get(selectedMeeting.leadId)?.email || '').trim().toLowerCase();
    }
    return String(
      selectedMeeting.contactEmail
      || getMeetingTagValue(selectedMeeting, 'meta_contact_email:')
      || ''
    ).trim().toLowerCase();
  }, [selectedMeeting, contactMap, leadMap]);

  const shouldLoadActivityEmailThreads =
    showActivitySidebar
    && activitySidebarTab === 'email'
    && activityEmailAddress.length > 0;

  const {
    data: activityInboxEmailThreadsData,
    isLoading: isLoadingActivityInboxEmailThreads,
    isFetching: isRefreshingActivityInboxEmailThreads,
    error: activityInboxEmailThreadsError,
    refetch: refetchActivityInboxEmailThreads,
  } = useGetEmailsQuery(
    {
      emailAccount: 'all',
      folder: 'inbox',
      limit: 200,
    },
    {
      skip: !shouldLoadActivityEmailThreads,
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: activitySentEmailThreadsData,
    isLoading: isLoadingActivitySentEmailThreads,
    isFetching: isRefreshingActivitySentEmailThreads,
    error: activitySentEmailThreadsError,
    refetch: refetchActivitySentEmailThreads,
  } = useGetEmailsQuery(
    {
      emailAccount: 'all',
      folder: 'sent',
      limit: 200,
    },
    {
      skip: !shouldLoadActivityEmailThreads,
      refetchOnMountOrArgChange: true,
    }
  );

  const activityEmailThreadsData = useMemo(
    () => mergeEmailResponses(activityInboxEmailThreadsData, activitySentEmailThreadsData),
    [activityInboxEmailThreadsData, activitySentEmailThreadsData]
  );
  const activityEmailThreads = useMemo(
    () => buildEmailThreadsForEntity(activityEmailThreadsData, activityEmailAddress),
    [activityEmailThreadsData, activityEmailAddress]
  );
  const isLoadingActivityEmailThreads = isLoadingActivityInboxEmailThreads || isLoadingActivitySentEmailThreads;
  const isRefreshingActivityEmailThreads = isRefreshingActivityInboxEmailThreads || isRefreshingActivitySentEmailThreads;
  const activityEmailThreadsError =
    activityInboxEmailThreadsError && activitySentEmailThreadsError ? activityInboxEmailThreadsError : null;
  const refetchActivityEmailThreads = (): void => {
    refetchActivityInboxEmailThreads();
    refetchActivitySentEmailThreads();
  };

  useEffect(() => {
    if (!selectedMeeting) {
      setActivitySidebarNoteDraft('');
      setActivitySidebarTab('notes');
      setActivitySidebarHoveredField(null);
      setActivitySidebarEditingFieldId(null);
      setActivitySidebarFieldDraftValue('');
      return;
    }
    setActivitySidebarNoteDraft(String(selectedMeeting.notes || ''));
    setActivitySidebarTab('notes');
    setActivitySidebarHoveredField(null);
    setActivitySidebarEditingFieldId(null);
    setActivitySidebarFieldDraftValue('');
  }, [selectedMeeting]);

  const isSidebarNoteDirty = useMemo(
    () => String(activitySidebarNoteDraft || '') !== String(selectedMeeting?.notes || ''),
    [activitySidebarNoteDraft, selectedMeeting?.notes]
  );

  const selfOwnerBaseLabel = useMemo(() => {
    const explicitName = String(currentUser?.name || '').trim();
    const combinedName = `${String(currentUser?.firstName || '').trim()} ${String(currentUser?.lastName || '').trim()}`.trim();
    return explicitName || combinedName || DEFAULT_SELF_OWNER_LABEL;
  }, [currentUser?.firstName, currentUser?.lastName, currentUser?.name]);
  const selfOwnerFilterLabel = useMemo(
    () => `${selfOwnerBaseLabel} (you)`,
    [selfOwnerBaseLabel]
  );

  const selectedMeetingVisibilityValue = selectedMeeting?.visibility === 'global' ? 'global' : 'personal';
  const selectedMeetingOwnerDisplayLabel =
    selectedMeetingVisibilityValue === 'global' ? DEFAULT_OWNER_FILTER_LABEL : selfOwnerBaseLabel;
  const selectedMeetingVisibilityDisplayLabel =
    selectedMeetingVisibilityValue === 'personal'
      ? 'Personal (private)'
      : 'Global (visible for everyone)';
  const detailOwnerOptions = useMemo(
    () => [DEFAULT_OWNER_FILTER_LABEL, selfOwnerBaseLabel],
    [selfOwnerBaseLabel]
  );

  const toDateInputValue = (dateValue?: string): string => {
    if (!dateValue) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue)) return dateValue;
    const usMatch = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (usMatch) {
      const month = usMatch[1].padStart(2, '0');
      const day = usMatch[2].padStart(2, '0');
      const year = usMatch[3];
      return `${year}-${month}-${day}`;
    }
    const parsed = new Date(dateValue);
    if (!Number.isNaN(parsed.getTime())) {
      return getDateKeyInTimezone(parsed, tenantTimezone) || '';
    }
    return '';
  };

  const ownerOptions = useMemo(() => {
    const ownerMap = new Map<string, string>();
    ownerMap.set(normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL), DEFAULT_OWNER_FILTER_LABEL);
    ownerMap.set(normalizeOwnerKey(selfOwnerFilterLabel), selfOwnerFilterLabel);

    meetings.forEach((meeting) => {
      const ownerLabel = String(meeting.owner || '').trim();
      if (!ownerLabel) return;
      ownerMap.set(normalizeOwnerKey(ownerLabel), ownerLabel);
    });

    return Array.from(ownerMap.entries())
      .map(([key, label]) => ({ key, label }))
      .sort((a, b) => {
        if (a.key === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) return -1;
        if (b.key === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) return 1;
        return a.label.localeCompare(b.label);
      });
  }, [meetings, selfOwnerFilterLabel]);

  const selectedOwnerButtonLabel = useMemo(() => {
    const normalizedSelected = normalizeOwnerKey(selectedOwner);
    if (!selectedOwner || normalizedSelected === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) {
      return DEFAULT_OWNER_FILTER_LABEL;
    }
    return String(selectedOwner).replace(/\(\s*you\s*\)/i, '').trim() || DEFAULT_OWNER_FILTER_LABEL;
  }, [selectedOwner]);

  const normalizedOwnerFilterSearchQuery = ownerFilterSearchQuery.trim().toLowerCase();

  const visibleOwnerOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return ownerOptions;
    return ownerOptions.filter((option) => option.label.toLowerCase().includes(normalizedOwnerFilterSearchQuery));
  }, [ownerOptions, normalizedOwnerFilterSearchQuery]);

  const visibleVisibilityFilterOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return ACTIVITY_VISIBILITY_FILTER_OPTIONS;
    return ACTIVITY_VISIBILITY_FILTER_OPTIONS.filter((option) => {
      const searchText = `${option.label} ${option.description}`.toLowerCase();
      return searchText.includes(normalizedOwnerFilterSearchQuery);
    });
  }, [normalizedOwnerFilterSearchQuery]);

  const favoriteOwnerOptions = useMemo(() => {
    if (!favoriteOwnerKeys.length) return [];
    const favoriteKeySet = new Set(favoriteOwnerKeys.map((entry) => normalizeOwnerKey(entry)));
    return ownerOptions.filter((option) => favoriteKeySet.has(option.key));
  }, [ownerOptions, favoriteOwnerKeys]);

  const favoriteVisibilityFilterOptions = useMemo(() => {
    if (!favoriteVisibilityFilterIds.length) return [];
    const favoriteSet = new Set<ActivityVisibilityFilterId>(favoriteVisibilityFilterIds);
    return ACTIVITY_VISIBILITY_FILTER_OPTIONS.filter((option) => favoriteSet.has(option.id));
  }, [favoriteVisibilityFilterIds]);

  const allActivityTags = useMemo(() => {
    const tagSet = new Set<string>();
    meetings.forEach((meeting) => {
      const tags = Array.isArray(meeting.tags) ? meeting.tags : [];
      tags.forEach((tag) => {
        const normalized = String(tag || '').trim();
        if (
          normalized
          && !normalized.startsWith('meta_')
          && !normalized.startsWith('activity:')
          && !normalized.startsWith('purpose:')
        ) {
          tagSet.add(normalized);
        }
      });
    });
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
  }, [meetings]);

  const allAvailableLabels = useMemo(() => {
    const candidates: string[] = [
      ...Object.keys(getSharedLabelColors()),
      ...allActivityTags,
      ...customLabels,
    ];
    return mergeLabelCatalogCandidates(candidates);
  }, [allActivityTags, customLabels]);

  const visibleFavoriteOwnerOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return favoriteOwnerOptions;
    return favoriteOwnerOptions.filter((option) => option.label.toLowerCase().includes(normalizedOwnerFilterSearchQuery));
  }, [favoriteOwnerOptions, normalizedOwnerFilterSearchQuery]);

  const visibleFavoriteVisibilityFilterOptions = useMemo(() => {
    if (!normalizedOwnerFilterSearchQuery) return favoriteVisibilityFilterOptions;
    return favoriteVisibilityFilterOptions.filter((option) => {
      const searchText = `${option.label} ${option.description}`.toLowerCase();
      return searchText.includes(normalizedOwnerFilterSearchQuery);
    });
  }, [favoriteVisibilityFilterOptions, normalizedOwnerFilterSearchQuery]);

  const now = useMemo(() => new Date(), [meetingsData]);
  const todayKey = getDateKeyInTimezone(now.toISOString(), tenantTimezone);
  const tomorrowKey = getDateKeyInTimezone(new Date(now.getTime() + 86400000).toISOString(), tenantTimezone);

  const filteredMeetings = useMemo(() => {
    let list = [...meetings];

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      list = list.filter((meeting) => {
        const title = (meeting.title || '').toLowerCase();
        const meetingOrganization = String(meeting.organizationName || getMeetingTagValue(meeting, 'meta_org:') || '').trim();
        const organization =
          meetingOrganization
          || (meeting.clientId ? clientMap.get(meeting.clientId)?.organization || clientMap.get(meeting.clientId)?.name : null)
          || (meeting.leadId ? leadMap.get(meeting.leadId)?.organization : null)
          || (meeting.contactId ? contactMap.get(meeting.contactId)?.organization : null)
          || '';
        const meetingContactName = String(meeting.contactName || getMeetingTagValue(meeting, 'meta_contact_name:') || '').trim();
        const contactName = meeting.contactId
          ? (`${contactMap.get(meeting.contactId)?.firstName || ''} ${contactMap.get(meeting.contactId)?.lastName || ''}`.trim() || meetingContactName)
          : (meeting.leadId ? leadMap.get(meeting.leadId)?.name : '')
            || meetingContactName;
        const dealName = meeting.dealId
          ? dealMap.get(meeting.dealId)?.name || ''
          : meeting.leadId
            ? leadMap.get(meeting.leadId)?.name || ''
            : String(meeting.dealLeadLabel || getMeetingTagValue(meeting, 'meta_deal_lead:') || '');
        return (
          title.includes(query)
          || organization.toLowerCase().includes(query)
          || contactName.toLowerCase().includes(query)
          || dealName.toLowerCase().includes(query)
        );
      });
    }

    if (typeFilter !== 'all') {
      list = list.filter((meeting) => {
        const type = String(meeting.meetingType || '').toLowerCase();
        const title = String(meeting.title || '').toLowerCase();
        if (typeFilter === 'call') return type === 'phone' || title.includes('call');
        if (typeFilter === 'meeting') return type === 'in-person' || type === 'video';
        if (typeFilter === 'email') return type === 'email' || title.includes('email');
        return true;
      });
    }

    if (selectedTag) {
      list = list.filter((meeting) =>
        Array.isArray(meeting.tags)
        && meeting.tags.some((tag) => {
          const normalized = String(tag || '').trim();
          if (
            !normalized
            || normalized.startsWith('meta_')
            || normalized.startsWith('activity:')
            || normalized.startsWith('purpose:')
          ) {
            return false;
          }
          return normalized.toLowerCase() === selectedTag.trim().toLowerCase();
        })
      );
    }

    if (selectedPriority) {
      list = list.filter(
        (meeting) => getMeetingPriorityValue(meeting) === selectedPriority.trim().toLowerCase()
      );
    }

    if (dateFilter !== 'all') {
      list = list.filter((meeting) => {
        const isDone = getMeetingDoneValue(meeting);
        if (dateFilter === 'done') return isDone;
        if (isDone) return false;

        const start = meeting.startTime ? new Date(meeting.startTime).getTime() : 0;
        const key = meeting.startTime ? getDateKeyInTimezone(meeting.startTime, tenantTimezone) : '';
        if (dateFilter === 'overdue') return Boolean(key && key < todayKey);
        // 'todo' is the default view (labeled "All" in the UI) and means every
        // open activity, including overdue. Overdue items are still things you
        // have to do — they shouldn't disappear from the default view the
        // moment their due date passes. The 'overdue' filter above is the way
        // to narrow to just-overdue when needed. Items missing a date stay
        // visible too (they're open work).
        if (dateFilter === 'todo') return true;
        if (dateFilter === 'today') return key === todayKey;
        if (dateFilter === 'tomorrow') return key === tomorrowKey;
        if (dateFilter === 'this-week' || dateFilter === 'next-week') {
          const startOfWeek = new Date(now);
          startOfWeek.setDate(now.getDate() - now.getDay());
          startOfWeek.setHours(0, 0, 0, 0);
          const endOfThisWeek = new Date(startOfWeek);
          endOfThisWeek.setDate(startOfWeek.getDate() + 7);
          const startOfNextWeek = new Date(endOfThisWeek);
          const endOfNextWeek = new Date(startOfNextWeek);
          endOfNextWeek.setDate(endOfNextWeek.getDate() + 7);
          if (dateFilter === 'this-week') return start >= startOfWeek.getTime() && start < endOfThisWeek.getTime();
          return start >= startOfNextWeek.getTime() && start < endOfNextWeek.getTime();
        }
        return true;
      });
    }

    const normalizedSelectedOwner = normalizeOwnerKey(selectedOwner);
    if (normalizedSelectedOwner && normalizedSelectedOwner !== normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) {
      list = list.filter((meeting) => {
        const ownerLabel = String(meeting.owner || '').trim();
        if (ownerLabel) return normalizeOwnerKey(ownerLabel) === normalizedSelectedOwner;
        if (normalizedSelectedOwner === normalizeOwnerKey(selfOwnerFilterLabel)) {
          return String(meeting.visibility || '').toLowerCase() !== 'global';
        }
        return false;
      });
    }

    if (selectedVisibilityFilterId === 'global') {
      list = list.filter((meeting) => String(meeting.visibility || '').toLowerCase() === 'global');
    } else if (selectedVisibilityFilterId === 'personal') {
      list = list.filter((meeting) => String(meeting.visibility || '').toLowerCase() !== 'global');
    }

    const priorityRank = (meeting: Meeting): number => {
      const p = getMeetingPriorityValue(meeting);
      if (p === 'high') return 0;
      if (p === 'normal') return 1;
      return 2; // low
    };

    list.sort((a, b) => {
      const byPriority = priorityRank(a) - priorityRank(b);
      if (byPriority !== 0) return byPriority;
      return new Date(a.startTime || 0).getTime() - new Date(b.startTime || 0).getTime();
    });
    return list;
  }, [
    meetings,
    searchQuery,
    typeFilter,
    dateFilter,
    selectedPriority,
    selectedTag,
    clientMap,
    leadMap,
    contactMap,
    dealMap,
    tenantTimezone,
    now,
    todayKey,
    tomorrowKey,
    selectedOwner,
    selectedVisibilityFilterId,
    selfOwnerFilterLabel,
  ]);

  // Bulk-selection helpers — derived from filteredMeetings + selectedRowIds.
  // headerCheckboxState drives the column-1 header checkbox: 'none' | 'some' | 'all'
  // 'some' renders as the indeterminate dash (DOM property; see ref below).
  const filteredIds = useMemo(() => filteredMeetings.map((m) => m.id), [filteredMeetings]);
  const selectedFilteredCount = useMemo(
    () => filteredIds.reduce((n, id) => (selectedRowIds.has(id) ? n + 1 : n), 0),
    [filteredIds, selectedRowIds]
  );
  const headerCheckboxState: 'none' | 'some' | 'all' =
    selectedFilteredCount === 0
      ? 'none'
      : selectedFilteredCount === filteredIds.length
        ? 'all'
        : 'some';

  const handleToggleRowSelected = (meetingId: string) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(meetingId)) next.delete(meetingId);
      else next.add(meetingId);
      return next;
    });
  };

  // Select-all toggles only the rows currently visible (post-filter), so the
  // header checkbox always behaves on what the operator can actually see.
  const handleToggleSelectAll = () => {
    setSelectedRowIds((prev) => {
      if (headerCheckboxState === 'all') {
        // Deselect everything in the visible set; preserve any selections that
        // are currently filtered out so they survive a filter toggle.
        const next = new Set(prev);
        for (const id of filteredIds) next.delete(id);
        return next;
      }
      const next = new Set(prev);
      for (const id of filteredIds) next.add(id);
      return next;
    });
  };

  const handleClearSelection = () => setSelectedRowIds(new Set());

  // Bulk visibility update. Fans out updateMeeting() in parallel; reports the
  // first failure (if any) so the operator knows partial completion happened.
  const handleBulkSetVisibility = async (visibility: 'personal' | 'global') => {
    const ids = Array.from(selectedRowIds);
    if (ids.length === 0) return;
    setIsBulkUpdating(true);
    try {
      const results = await Promise.allSettled(
        ids.map((id) => updateMeeting({ meetingId: id, visibility }).unwrap())
      );
      const failed = results.filter((r) => r.status === 'rejected') as PromiseRejectedResult[];
      if (failed.length > 0) {
        const firstReason = failed[0]?.reason;
        const reasonText = firstReason?.data?.message || firstReason?.message || 'Unknown error';
        alert(`Updated ${ids.length - failed.length} of ${ids.length}. ${failed.length} failed (first error: ${reasonText}).`);
      }
      setSelectedRowIds(new Set());
      await refetchMeetings();
    } finally {
      setIsBulkUpdating(false);
    }
  };

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVITY_OWNER_FAVORITES_STORAGE_KEY, JSON.stringify(favoriteOwnerKeys));
    } catch {
      // Ignore persistence failures in restricted browser modes.
    }
  }, [favoriteOwnerKeys]);

  useEffect(() => {
    try {
      localStorage.setItem(ACTIVITY_FILTER_FAVORITES_STORAGE_KEY, JSON.stringify(favoriteVisibilityFilterIds));
    } catch {
      // Ignore persistence failures in restricted browser modes.
    }
  }, [favoriteVisibilityFilterIds]);

  useEffect(() => {
    if (normalizeOwnerKey(selectedOwner) === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) return;
    const selectedExists = ownerOptions.some((option) => option.key === normalizeOwnerKey(selectedOwner));
    if (!selectedExists) {
      setSelectedOwner(DEFAULT_OWNER_FILTER_LABEL);
    }
  }, [ownerOptions, selectedOwner]);

  const toggleFavoriteOwner = (ownerKey: string) => {
    const normalizedKey = normalizeOwnerKey(ownerKey);
    if (!normalizedKey || normalizedKey === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL)) return;
    setFavoriteOwnerKeys((prev) => {
      const normalizedPrev = prev.map((entry) => normalizeOwnerKey(entry));
      if (normalizedPrev.includes(normalizedKey)) {
        return prev.filter((entry) => normalizeOwnerKey(entry) !== normalizedKey);
      }
      return [...normalizedPrev, normalizedKey];
    });
  };

  const toggleFavoriteVisibilityFilter = (filterId: ActivityVisibilityFilterId) => {
    if (filterId === 'all') return;
    setFavoriteVisibilityFilterIds((prev) => {
      if (prev.includes(filterId)) {
        return prev.filter((entry) => entry !== filterId);
      }
      return [...prev, filterId];
    });
  };

  const calendarActivitiesByDate = useMemo(() => {
    const grouped = new Map<string, Meeting[]>();
    filteredMeetings.forEach((meeting) => {
      const dateKey = getDateKeyInTimezone(meeting.startTime, tenantTimezone);
      if (!dateKey) return;
      const existing = grouped.get(dateKey) || [];
      existing.push(meeting);
      grouped.set(dateKey, existing);
    });
    grouped.forEach((items) => items.sort((a, b) => new Date(a.startTime || 0).getTime() - new Date(b.startTime || 0).getTime()));
    return grouped;
  }, [filteredMeetings, tenantTimezone]);

  const calendarMonthLabel = useMemo(
    () => toLocaleDateStringInTimezone(calendarMonth, 'en-US', { month: 'long', year: 'numeric' }, tenantTimezone),
    [calendarMonth, tenantTimezone]
  );

  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1, 12, 0, 0, 0);
    const lastDay = new Date(year, month + 1, 0, 12, 0, 0, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    const days: Array<Date | null> = [];

    for (let i = 0; i < startingDayOfWeek; i += 1) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day += 1) {
      days.push(new Date(year, month, day, 12, 0, 0, 0));
    }
    while (days.length % 7 !== 0) {
      days.push(null);
    }
    return days;
  }, [calendarMonth]);

  const todayCalendarKey = useMemo(
    () => getDateKeyInTimezone(new Date().toISOString(), tenantTimezone),
    [tenantTimezone]
  );

  const filteredDealsAndLeads = useMemo(() => {
    const query = dealLeadSearch.toLowerCase().trim();
    const dealItems: DealLeadOption[] = deals.map((deal) => ({
      id: deal.id,
      type: 'deal',
      label: deal.name || 'Untitled deal',
    }));
    const leadItems: DealLeadOption[] = leads.map((lead) => ({
      id: lead.id,
      type: 'lead',
      label: lead.organization ? `${lead.name || 'Untitled'} (${lead.organization})` : (lead.name || 'Untitled lead'),
    }));
    const combined = [...dealItems, ...leadItems];
    if (!query) return combined.slice(0, 12);
    return combined.filter((item) => item.label.toLowerCase().includes(query)).slice(0, 12);
  }, [deals, leads, dealLeadSearch]);

  type PeopleOption = { id: string; type: 'contact' | 'client' | 'lead'; label: string };

  const filteredPeople = useMemo((): PeopleOption[] => {
    const query = peopleSearch.toLowerCase().trim();

    const contactOptions = contacts.map((contact) => {
      const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
      const label = contact.organization
        ? `${fullName || 'Untitled contact'} (${contact.organization})`
        : (fullName || 'Untitled contact');
      const searchText = `${fullName} ${contact.organization || ''} ${contact.email || ''}`.toLowerCase();
      return { id: contact.id, type: 'contact' as const, label, searchText };
    });

    const clientOptions = clients.map((client) => {
      const personName = String(client.name || '').trim();
      const firstLast = `${(client as { firstName?: string }).firstName || ''} ${(client as { lastName?: string }).lastName || ''}`.trim();
      const resolvedName = (firstLast || personName || 'Untitled client').trim();
      const org = String(client.organization || '').trim();
      const label = org ? `${resolvedName} (${org})` : resolvedName;
      const searchText = `${resolvedName} ${personName} ${org} ${String(client.email || '').trim()}`.toLowerCase();
      return { id: client.id, type: 'client' as const, label, searchText };
    });

    const leadOptions = leads.map((lead) => {
      const name = String(lead.name || '').trim() || 'Untitled lead';
      const org = String(lead.organization || '').trim();
      const label = org ? `${name} (${org})` : name;
      const searchText = `${name} ${org} ${String(lead.email || '').trim()}`.toLowerCase();
      return { id: lead.id, type: 'lead' as const, label, searchText };
    });

    const combined = [...clientOptions, ...contactOptions, ...leadOptions];
    if (!query) return combined.slice(0, 12).map(({ id, type, label }) => ({ id, type, label }));
    return combined
      .filter((item) => item.searchText.includes(query))
      .slice(0, 12)
      .map(({ id, type, label }) => ({ id, type, label }));
  }, [clients, contacts, leads, peopleSearch]);

  const filteredOrganizations = useMemo((): OrganizationOption[] => {
    const query = orgSearch.toLowerCase().trim();
    const seen = new Set<string>();
    const options: OrganizationOption[] = [];

    const addOption = (option: OrganizationOption): void => {
      const key = option.label.trim().toLowerCase();
      if (!key || seen.has(key)) return;
      seen.add(key);
      options.push(option);
    };

    accounts.forEach((account) => {
      const label = String(account.name || '').trim();
      if (!label) return;
      addOption({ id: account.id, type: 'account', label });
    });

    contacts.forEach((contact) => {
      const label = String(contact.organization || '').trim();
      if (!label) return;
      addOption({ id: contact.id, type: 'contactOrg', label });
    });

    leads.forEach((lead) => {
      const label = String(lead.organization || '').trim();
      if (!label) return;
      addOption({ id: lead.id, type: 'leadOrg', label });
    });

    const sorted = options.sort((a, b) => a.label.localeCompare(b.label));
    if (!query) return sorted.slice(0, 20);
    return sorted.filter((item) => item.label.toLowerCase().includes(query)).slice(0, 20);
  }, [accounts, contacts, leads, orgSearch]);

  const dealLeadDisplayLabel = useMemo(() => {
    if (activityForm.dealId) {
      const deal = deals.find((item) => item.id === activityForm.dealId);
      return deal?.name || 'Deal';
    }
    if (activityForm.leadId) {
      const lead = leads.find((item) => item.id === activityForm.leadId);
      return lead ? (lead.organization ? `${lead.name || ''} (${lead.organization})` : (lead.name || 'Lead')) : 'Lead';
    }
    return String(activityForm.dealLeadLabel || '');
  }, [activityForm.dealId, activityForm.leadId, activityForm.dealLeadLabel, deals, leads]);

  const contactDisplayLabel = useMemo(() => {
    if (activityForm.contactId) {
      const contact = contacts.find((item) => item.id === activityForm.contactId);
      if (!contact) return 'Contact';
      const name = `${contact.firstName || ''} ${contact.lastName || ''}`.trim();
      return contact.organization ? `${name || 'Contact'} (${contact.organization})` : (name || 'Contact');
    }

    if (activityForm.clientId) {
      const client = clients.find((item) => item.id === activityForm.clientId);
      const personName = String(client?.name || '').trim() || String(activityForm.contactName || '').trim();
      const org = String(client?.organization || '').trim();
      if (!personName && !org) return 'People';
      return org ? `${personName || 'Client'} (${org})` : (personName || 'Client');
    }

    if (activityForm.leadId) {
      const lead = leads.find((item) => item.id === activityForm.leadId);
      const personName = String(lead?.name || '').trim() || String(activityForm.contactName || '').trim();
      const org = String(lead?.organization || '').trim();
      if (!personName && !org) return 'People';
      return org ? `${personName || 'Lead'} (${org})` : (personName || 'Lead');
    }

    return String(activityForm.contactName || '');
  }, [activityForm.clientId, activityForm.contactId, activityForm.contactName, activityForm.leadId, clients, contacts, leads]);

  const organizationDisplayLabel = useMemo(() => {
    if (!activityForm.accountId) return String(activityForm.organizationName || '');
    const account = accounts.find((item) => item.id === activityForm.accountId);
    return String(account?.name || '').trim() || String(activityForm.organizationName || '').trim() || 'Organization';
  }, [activityForm.accountId, activityForm.organizationName, accounts]);

  const getOrganization = (meeting: Meeting): string => {
    const meetingOrganization = String(meeting.organizationName || getMeetingTagValue(meeting, 'meta_org:') || '').trim();
    if (meeting.accountId) {
      return meetingOrganization
        || String(accountMap.get(meeting.accountId)?.name || '').trim()
        || '—';
    }
    if (meeting.clientId) {
      return meetingOrganization
        || String(clientMap.get(meeting.clientId)?.organization || '').trim()
        || String(clientMap.get(meeting.clientId)?.name || '').trim()
        || '—';
    }
    if (meeting.leadId) return meetingOrganization || leadMap.get(meeting.leadId)?.organization || leadMap.get(meeting.leadId)?.name || '—';
    if (meeting.contactId) return meetingOrganization || contactMap.get(meeting.contactId)?.organization || '—';
    return meetingOrganization || '—';
  };

  const getContactPerson = (meeting: Meeting): string => {
    const meetingContactName = String(meeting.contactName || getMeetingTagValue(meeting, 'meta_contact_name:') || '').trim();
    if (meeting.contactId) {
      const contact = contactMap.get(meeting.contactId);
      return (contact ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() : '') || meetingContactName || '—';
    }
    if (meeting.leadId) return leadMap.get(meeting.leadId)?.name || meetingContactName || '—';
    if (meeting.clientId) {
      return meetingContactName || String(clientMap.get(meeting.clientId)?.name || '').trim() || '—';
    }
    return meetingContactName || '—';
  };

  const getEmail = (meeting: Meeting): string => {
    const meetingContactEmail = String(meeting.contactEmail || getMeetingTagValue(meeting, 'meta_contact_email:') || '').trim();
    if (meeting.contactId) return contactMap.get(meeting.contactId)?.email || meetingContactEmail || '—';
    if (meeting.leadId) return leadMap.get(meeting.leadId)?.email || meetingContactEmail || '—';
    if (meeting.clientId) return meetingContactEmail || clientMap.get(meeting.clientId)?.email || '—';
    return meetingContactEmail || '—';
  };

  const getDealName = (meeting: Meeting): string => {
    if (meeting.dealId) return dealMap.get(meeting.dealId)?.name || '—';
    if (meeting.leadId) return leadMap.get(meeting.leadId)?.name || '—';
    return String(meeting.dealLeadLabel || getMeetingTagValue(meeting, 'meta_deal_lead:') || '').trim() || '—';
  };

  const getActivityVisibleLabels = (meeting: Meeting | null): string[] => {
    if (!meeting || !Array.isArray(meeting.tags)) return [];
    return meeting.tags
      .map((tag) => String(tag || '').trim())
      .filter((tag) => (
        tag.length > 0
        && !tag.startsWith('meta_')
        && !tag.startsWith('activity:')
        && !tag.startsWith('purpose:')
      ));
  };

  const getActivityHiddenTags = (meeting: Meeting | null): string[] => {
    if (!meeting || !Array.isArray(meeting.tags)) return [];
    return meeting.tags
      .map((tag) => String(tag || '').trim())
      .filter((tag) => (
        tag.length > 0
        && (
          tag.startsWith('meta_')
          || tag.startsWith('activity:')
          || tag.startsWith('purpose:')
        )
      ));
  };

  const selectedActivityLabels = useMemo(
    () => getActivityVisibleLabels(selectedMeeting),
    [selectedMeeting]
  );

  const getActivityPersonTarget = () => {
    if (selectedMeeting?.contactId && selectedActivityContact) {
      return { type: 'contact' as const, id: selectedMeeting.contactId, record: selectedActivityContact };
    }
    if (selectedMeeting?.leadId && selectedActivityLead) {
      return { type: 'lead' as const, id: selectedMeeting.leadId, record: selectedActivityLead };
    }
    // Some activities are linked to a Client (clientId) with no Contact/Lead.
    // In that case, use the Client record as the "Person" source so fields
    // like name/contactStage/email/phone stay consistent across pages.
    // Note: the client record may not be hydrated yet; still return a target so edits
    // update the canonical Client entity (and the UI catches up after refetch).
    if (selectedMeeting?.clientId) {
      return { type: 'client' as const, id: selectedMeeting.clientId, record: selectedActivityClient || {} };
    }
    return null;
  };

  const getActivityOrganizationTarget = () => {
    if (selectedMeeting?.clientId) {
      return { type: 'client' as const, id: selectedMeeting.clientId, record: selectedActivityClient || {} };
    }
    if (selectedMeeting?.leadId && selectedActivityLead) {
      return { type: 'lead' as const, id: selectedMeeting.leadId, record: selectedActivityLead };
    }
    if (selectedMeeting?.contactId && selectedActivityContact) {
      return { type: 'contact' as const, id: selectedMeeting.contactId, record: selectedActivityContact };
    }
    return null;
  };

  const getActivityDetailsTarget = () => {
    if (selectedMeeting?.clientId) {
      return { type: 'client' as const, id: selectedMeeting.clientId, record: selectedActivityClient || {} };
    }
    if (selectedMeeting?.leadId && selectedActivityLead) {
      return { type: 'lead' as const, id: selectedMeeting.leadId, record: selectedActivityLead };
    }
    if (selectedMeeting?.contactId && selectedActivityContact) {
      return { type: 'contact' as const, id: selectedMeeting.contactId, record: selectedActivityContact };
    }
    return null;
  };

  const getActivityFieldValue = (fieldId: ActivityEditableFieldId): string => {
    const personTarget = getActivityPersonTarget();
    const organizationTarget = getActivityOrganizationTarget();
    const detailsTarget = getActivityDetailsTarget();
    const personRecord: any = personTarget?.record || {};
    const organizationRecord: any = organizationTarget?.record || {};
    const detailsRecord: any = detailsTarget?.record || {};
    const selectedMeetingRecord: any = selectedMeeting || {};

    switch (fieldId) {
      case 'owner':
        return selectedMeetingOwnerDisplayLabel;
      case 'visibility':
        return selectedMeetingVisibilityDisplayLabel;
      case 'name':
        const resolvedPersonName = selectedMeeting ? getContactPerson(selectedMeeting) : '';
        const normalizedResolvedPersonName = resolvedPersonName === '—' ? '' : resolvedPersonName;
        if (personTarget?.type === 'contact') {
          return `${personRecord.firstName || ''} ${personRecord.lastName || ''}`.trim() || normalizedResolvedPersonName;
        }
        if (personTarget?.type === 'lead') {
          return String(personRecord.name || '').trim() || normalizedResolvedPersonName;
        }
        if (personTarget?.type === 'client') {
          const meetingContactName = selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_contact_name:') : '';
          return String(selectedMeetingRecord.contactName || meetingContactName || personRecord.name || normalizedResolvedPersonName || '').trim();
        }
        return String(selectedMeeting?.contactName || normalizedResolvedPersonName || '').trim();
      case 'email':
        return normalizeActivitySidebarValue(selectedMeeting ? getEmail(selectedMeeting) : '');
      case 'phone':
        return String(
          personRecord.phone
          || selectedMeetingRecord.phone
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_phone:') : '')
          || ''
        ).trim();
      case 'priority':
        return getMeetingPriorityValue(selectedMeetingRecord);
      case 'contactStage':
        return String(
          personRecord.contactStage
          || selectedMeetingRecord.contactStage
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_contact_stage:') : '')
          || ''
        ).trim();
      case 'value':
        return String(
          detailsRecord.value
          || selectedMeetingRecord.value
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_value:') : '')
          || ''
        ).trim();
      case 'expectedCloseDate':
        return String(
          detailsRecord.expectedCloseDate
          || selectedMeetingRecord.expectedCloseDate
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_expected_close_date:') : '')
          || ''
        ).trim();
      case 'businessUnit':
        return String(
          detailsRecord.businessUnit
          || selectedMeetingRecord.businessUnit
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_business_unit:') : '')
          || ''
        ).trim() || (detailsRecord.inStore || selectedMeetingRecord.inStore ? 'In-Store' : '');
      case 'sourceOrigin':
        return String(
          detailsRecord.sourceOrigin
          || selectedMeetingRecord.sourceOrigin
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_source_origin:') : '')
          || ''
        ).trim();
      case 'sourceChannel':
        return String(
          detailsRecord.sourceChannel
          || selectedMeetingRecord.sourceChannel
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_source_channel:') : '')
          || ''
        ).trim();
      case 'sourceChannelId':
        return String(
          detailsRecord.sourceChannelId
          || selectedMeetingRecord.sourceChannelId
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_source_channel_id:') : '')
          || ''
        ).trim();
      case 'organization':
        const resolvedOrganization = selectedMeeting ? getOrganization(selectedMeeting) : '';
        const normalizedResolvedOrganization = resolvedOrganization === '—' ? '' : resolvedOrganization;
        if (organizationTarget?.type === 'client') {
          const meetingOrganization = selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_org:') : '';
          return String(
            selectedMeetingRecord.organizationName
            || meetingOrganization
            || organizationRecord.organization
            || organizationRecord.name
            || normalizedResolvedOrganization
            || ''
          ).trim();
        }
        if (organizationTarget?.type === 'lead') {
          return String(organizationRecord.organization || organizationRecord.name || normalizedResolvedOrganization || '').trim();
        }
        if (organizationTarget?.type === 'contact') {
          return String(organizationRecord.organization || normalizedResolvedOrganization || '').trim();
        }
        return String(
          selectedMeeting?.organizationName
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_org:') : '')
          || normalizedResolvedOrganization
          || ''
        ).trim();
      case 'accountStage':
        return String(
          organizationRecord.accountStage
          || selectedMeetingRecord.accountStage
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_account_stage:') : '')
          || ''
        ).trim();
      case 'address':
        return String(
          organizationRecord.address
          || selectedMeetingRecord.address
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_address:') : '')
          || ''
        ).trim();
      case 'website':
        return String(
          organizationRecord.website
          || selectedMeetingRecord.website
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_website:') : '')
          || ''
        ).trim();
      case 'linkedInProfile':
        return String(
          organizationRecord.linkedInProfile
          || selectedMeetingRecord.linkedInProfile
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_linkedin_profile:') : '')
          || ''
        ).trim();
      case 'numberOfEmployees':
        return String(
          organizationRecord.numberOfEmployees
          || selectedMeetingRecord.numberOfEmployees
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_number_of_employees:') : '')
          || ''
        ).trim();
      case 'annualRevenue':
        return String(
          organizationRecord.annualRevenue
          || selectedMeetingRecord.annualRevenue
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_annual_revenue:') : '')
          || ''
        ).trim();
      case 'industry':
        return String(
          organizationRecord.industry
          || selectedMeetingRecord.industry
          || (selectedMeeting ? getMeetingTagValue(selectedMeeting, 'meta_industry:') : '')
          || ''
        ).trim();
      case 'title':
        return String(personRecord.title || '').trim();
      case 'workPhone':
        return String(personRecord.workPhone || '').trim();
      case 'cellPhone':
        return String(personRecord.cellPhone || '').trim();
      case 'assistantName':
        return String(personRecord.assistantName || '').trim();
      case 'assistantPhone':
        return String(personRecord.assistantPhone || '').trim();
      case 'assistantEmail':
        return String(personRecord.assistantEmail || '').trim();
      default:
        return '';
    }
  };

  const normalizeActivitySidebarValue = (value: string): string => {
    const normalized = String(value || '').trim();
    return normalized === '—' ? '' : normalized;
  };

  const activitySidebarIconStyle: React.CSSProperties = {
    color: '#6c757d',
    width: 16,
    display: 'inline-flex',
    justifyContent: 'center',
    flexShrink: 0,
  };

  const startEditingSidebarField = (fieldId: ActivityEditableFieldId) => {
    const currentValue = getActivityFieldValue(fieldId);

    if (fieldId === 'owner') {
      setActivitySidebarFieldDraftValue(selectedMeetingVisibilityValue === 'global' ? DEFAULT_OWNER_FILTER_LABEL : selfOwnerBaseLabel);
    } else if (fieldId === 'visibility') {
      setActivitySidebarFieldDraftValue(selectedMeetingVisibilityValue);
    } else if (fieldId === 'expectedCloseDate') {
      setActivitySidebarFieldDraftValue(toDateInputValue(currentValue));
    } else if (fieldId === 'businessUnit') {
      setActivitySidebarFieldDraftValue(currentValue);
    } else {
      setActivitySidebarFieldDraftValue(currentValue);
    }
    setActivitySidebarEditingFieldId(fieldId);
  };

  const cancelEditingSidebarField = () => {
    setActivitySidebarEditingFieldId(null);
    setActivitySidebarFieldDraftValue('');
  };

  const saveActivityEntityUpdate = async (
    target: { type: 'meeting' | 'lead' | 'contact' | 'client'; id: string },
    payload: Record<string, unknown>
  ) => {
    if (target.type === 'meeting') {
      await updateMeeting({ meetingId: target.id, ...payload }).unwrap();
      await refetchMeetings();
      setSelectedMeeting((prev) => (prev && prev.id === target.id ? ({ ...prev, ...payload } as Meeting) : prev));
      return;
    }

    if (target.type === 'lead') {
      await updateLead({ leadId: target.id, ...payload } as any).unwrap();
      await refetchLeads();
      return;
    }

    if (target.type === 'contact') {
      await updateContact({ contactId: target.id, ...payload } as any).unwrap();
      await refetchContacts();
      return;
    }

    await updateClient({ clientId: target.id, ...payload } as any).unwrap();
    await refetchClients();
  };

  const toggleActivityLabel = async (label: string) => {
    if (!selectedMeeting) return;
    const currentLabels = selectedActivityLabels;
    const hiddenTags = getActivityHiddenTags(selectedMeeting);
    const isAssigned = currentLabels.includes(label);
    const updatedLabels = isAssigned
      ? currentLabels.filter((item) => item !== label)
      : [...currentLabels, label];

    await saveActivityEntityUpdate(
      { type: 'meeting', id: selectedMeeting.id },
      { tags: [...hiddenTags, ...updatedLabels] }
    );
  };

  const handleCreateNewActivityLabel = async () => {
    if (!selectedMeeting) return;
    const rawValue = window.prompt('Label name');
    if (!rawValue) return;
    const nextLabel = rawValue.trim();
    if (!nextLabel) return;

    const existingLabel = allAvailableLabels.find(
      (label) => label.toLowerCase() === nextLabel.toLowerCase()
    );
    const finalLabel = existingLabel || nextLabel;

    if (!existingLabel) {
      const updatedCustomLabels = [...customLabels, finalLabel];
      setCustomLabels(updatedCustomLabels);
      localStorage.setItem('customLabels', JSON.stringify(updatedCustomLabels));
    }

    await toggleActivityLabel(finalLabel);
  };

  const handleCreateFilterLabel = () => {
    const rawValue = window.prompt('Label name');
    if (!rawValue) return;
    const nextLabel = rawValue.trim();
    if (!nextLabel) return;

    const existingLabel = allAvailableLabels.find(
      (label) => label.toLowerCase() === nextLabel.toLowerCase()
    );

    if (!existingLabel) {
      const updatedCustomLabels = [...customLabels, nextLabel];
      setCustomLabels(updatedCustomLabels);
      localStorage.setItem('customLabels', JSON.stringify(updatedCustomLabels));
    }

    setShowTagsFilter(false);
    setLabelsFilterSearchQuery('');
  };

  const getLabelVariant = (label: string): string => {
    const currentColors = getSharedLabelColors();
    return currentColors[label] || currentColors[label.toUpperCase()] || 'secondary';
  };

  const handleSaveSidebarField = async (fieldId: ActivityEditableFieldId) => {
    if (!selectedMeeting) return;

    if (fieldId === 'owner' || fieldId === 'visibility') {
      const currentVisibility = selectedMeetingVisibilityValue;
      const nextVisibility = fieldId === 'owner'
        ? (String(activitySidebarFieldDraftValue || '').trim().toLowerCase() === DEFAULT_OWNER_FILTER_LABEL.toLowerCase() ? 'global' : 'personal')
        : (String(activitySidebarFieldDraftValue || '').trim().toLowerCase() === 'personal' ? 'personal' : 'global');

      if (currentVisibility === nextVisibility) {
        cancelEditingSidebarField();
        return;
      }

      try {
        await saveActivityEntityUpdate({ type: 'meeting', id: selectedMeeting.id }, { visibility: nextVisibility });
        cancelEditingSidebarField();
      } catch (error: any) {
        console.error(`Error updating ${fieldId}:`, error);
        alert(`Failed to update ${fieldId === 'owner' ? 'Owner' : 'Visibility'}: ${error?.data?.message || error?.message || 'Unknown error'}`);
      }
      return;
    }

    const personTarget = getActivityPersonTarget();
    const organizationTarget = getActivityOrganizationTarget();
    const detailsTarget = getActivityDetailsTarget();

    const draftValue = ACTIVITY_EDITABLE_FIELD_CONFIG[fieldId].inputType === 'date'
      ? formatHtmlDateValueToUsStorage(activitySidebarFieldDraftValue)
      : activitySidebarFieldDraftValue.trim();
    const currentValue = normalizeActivitySidebarValue(getActivityFieldValue(fieldId));

    if (String(currentValue || '') === String(draftValue || '')) {
      cancelEditingSidebarField();
      return;
    }

    try {
      if (fieldId === 'priority') {
        const nextPriority = String(draftValue || 'normal').trim().toLowerCase();
        const normalized = nextPriority === 'high' || nextPriority === 'low' ? nextPriority : 'normal';
        const currentTags = Array.isArray(selectedMeeting.tags) ? selectedMeeting.tags.map((t) => String(t)) : [];
        const nextTags = upsertMeetingMetaTag(currentTags, 'meta_priority:', normalized);
        await saveActivityEntityUpdate(
          { type: 'meeting', id: selectedMeeting.id },
          { priority: normalized, tags: nextTags }
        );
        cancelEditingSidebarField();
        return;
      }

      if (fieldId === 'name') {
        let canonicalNameError: any = null;
        if (personTarget?.type === 'contact') {
          const nameValue = String(draftValue || '').trim();
          const [firstName = '', ...rest] = nameValue.split(/\s+/).filter(Boolean);
          try {
            await saveActivityEntityUpdate(personTarget, {
              firstName,
              lastName: rest.join(' '),
            });
          } catch (error: any) {
            canonicalNameError = error;
          }
        } else if (personTarget?.type === 'lead') {
          try {
            await saveActivityEntityUpdate(personTarget, { name: draftValue });
          } catch (error: any) {
            canonicalNameError = error;
          }
        } else if (personTarget?.type === 'client') {
          try {
            await saveActivityEntityUpdate(personTarget, { name: draftValue });
          } catch (error: any) {
            canonicalNameError = error;
          }
        }
        const currentTags = Array.isArray(selectedMeeting.tags) ? selectedMeeting.tags.map((t) => String(t)) : [];
        const nextTags = upsertMeetingMetaTag(currentTags, 'meta_contact_name:', draftValue);
        await saveActivityEntityUpdate({ type: 'meeting', id: selectedMeeting.id }, { contactName: draftValue, tags: nextTags });
        if (canonicalNameError) {
          console.warn('Canonical person update failed; meeting fallback saved instead.', canonicalNameError);
        }
        cancelEditingSidebarField();
        return;
      }

      if (fieldId === 'email') {
        if (personTarget) {
          await saveActivityEntityUpdate(personTarget, { email: draftValue });
        } else {
          await saveActivityEntityUpdate({ type: 'meeting', id: selectedMeeting.id }, { contactEmail: draftValue });
        }
        cancelEditingSidebarField();
        return;
      }

      if (fieldId === 'organization') {
        let canonicalOrganizationError: any = null;
        if (organizationTarget) {
          try {
            await saveActivityEntityUpdate(organizationTarget, { organization: draftValue });
          } catch (error: any) {
            canonicalOrganizationError = error;
          }
        }
        const currentTags = Array.isArray(selectedMeeting.tags) ? selectedMeeting.tags.map((t) => String(t)) : [];
        const nextTags = upsertMeetingMetaTag(currentTags, 'meta_org:', draftValue);
        await saveActivityEntityUpdate({ type: 'meeting', id: selectedMeeting.id }, { organizationName: draftValue, tags: nextTags });
        if (canonicalOrganizationError) {
          console.warn('Canonical organization update failed; meeting fallback saved instead.', canonicalOrganizationError);
        }
        cancelEditingSidebarField();
        return;
      }

      if (fieldId === 'businessUnit') {
        const target = detailsTarget || organizationTarget || personTarget || { type: 'meeting' as const, id: selectedMeeting.id };
        const nextBusinessUnit = String(draftValue || '').trim();
        const nextInStore = nextBusinessUnit.toLowerCase() === 'in-store' || nextBusinessUnit.toLowerCase() === 'in store';
        if (target.type === 'meeting') {
          const currentTags = Array.isArray(selectedMeeting.tags) ? selectedMeeting.tags.map((t) => String(t)) : [];
          const nextTags = upsertMeetingMetaTag(currentTags, 'meta_business_unit:', nextBusinessUnit);
          await saveActivityEntityUpdate(target, {
            businessUnit: nextBusinessUnit,
            inStore: nextInStore,
            tags: nextTags,
          });
        } else {
          await saveActivityEntityUpdate(target, {
            businessUnit: nextBusinessUnit,
            inStore: nextInStore,
          });
        }
        cancelEditingSidebarField();
        return;
      }

      // Auto-promote unlinked activity to a Lead when a stage is set.
      // The Rules Engine only consults leads/clients/contacts tables, so a
      // stage stored as a meeting meta-tag (the legacy fallback below) is
      // invisible to send-time blocks. Creating a real lead here keeps the
      // canonical CRM data and the Rules Engine in agreement.
      if (
        (fieldId === 'contactStage' || fieldId === 'accountStage')
        && !personTarget
        && !organizationTarget
        && !detailsTarget
      ) {
        const candidateEmail = String(
          selectedMeeting.contactEmail
          || getMeetingTagValue(selectedMeeting, 'meta_contact_email:')
          || ''
        ).trim().toLowerCase();
        const stageValue = String(draftValue || '').trim();
        const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(candidateEmail);

        if (candidateEmail && isValidEmail && stageValue) {
          const candidateName = (String(
            selectedMeeting.contactName
            || getMeetingTagValue(selectedMeeting, 'meta_contact_name:')
            || ''
          ).trim()) || candidateEmail.split('@')[0];

          const candidateOrganization = String(
            selectedMeeting.organizationName
            || getMeetingTagValue(selectedMeeting, 'meta_org:')
            || ''
          ).trim();

          try {
            const createResult: any = await createLead({
              name: candidateName,
              email: candidateEmail,
              ...(candidateOrganization ? { organization: candidateOrganization } : {}),
              ...(fieldId === 'contactStage' ? { contactStage: stageValue } : {}),
              ...(fieldId === 'accountStage' ? { accountStage: stageValue } : {}),
              sourceChannel: 'Activity',
              sourceOrigin: 'Activities',
              status: 'Lead',
              visibility: selectedMeetingVisibilityValue || 'global',
            }).unwrap();

            const createdLeadId =
              createResult?.data?.lead?.id
              || createResult?.lead?.id
              || createResult?.id;

            if (createdLeadId) {
              const currentTags = Array.isArray(selectedMeeting.tags) ? selectedMeeting.tags.map((t) => String(t)) : [];
              const prefix = META_TAG_PREFIX_BY_FIELD[fieldId];
              const nextTags = prefix ? upsertMeetingMetaTag(currentTags, prefix, draftValue) : currentTags;
              await saveActivityEntityUpdate(
                { type: 'meeting', id: selectedMeeting.id },
                { leadId: createdLeadId, tags: nextTags }
              );
              cancelEditingSidebarField();
              return;
            }
          } catch (autoPromoteError: any) {
            console.warn('Auto-promote to Lead failed; falling back to meeting meta-tag.', autoPromoteError);
          }
        }
      }

      const isPersonOnlyField =
        fieldId === 'title'
        || fieldId === 'workPhone'
        || fieldId === 'cellPhone'
        || fieldId === 'assistantName'
        || fieldId === 'assistantPhone'
        || fieldId === 'assistantEmail';

      const fieldTarget = isPersonOnlyField
        ? (personTarget || { type: 'meeting' as const, id: selectedMeeting.id })
        : fieldId === 'contactStage' || fieldId === 'phone'
          ? (personTarget || { type: 'meeting' as const, id: selectedMeeting.id })
          : fieldId === 'accountStage'
            || fieldId === 'address'
            || fieldId === 'website'
            || fieldId === 'linkedInProfile'
            || fieldId === 'numberOfEmployees'
            || fieldId === 'annualRevenue'
            || fieldId === 'industry'
              ? (organizationTarget || detailsTarget || { type: 'meeting' as const, id: selectedMeeting.id })
              : (detailsTarget || organizationTarget || personTarget || { type: 'meeting' as const, id: selectedMeeting.id });

      const prefix = META_TAG_PREFIX_BY_FIELD[fieldId];
      if (fieldTarget.type === 'meeting' && prefix) {
        const currentTags = Array.isArray(selectedMeeting.tags) ? selectedMeeting.tags.map((t) => String(t)) : [];
        const nextTags = upsertMeetingMetaTag(currentTags, prefix, draftValue);
        await saveActivityEntityUpdate(fieldTarget, { [fieldId]: draftValue, tags: nextTags });
      } else {
        await saveActivityEntityUpdate(fieldTarget, { [fieldId]: draftValue });
      }
      cancelEditingSidebarField();
    } catch (error: any) {
      console.error(`Error updating ${fieldId}:`, error);
      alert(`Failed to update ${ACTIVITY_EDITABLE_FIELD_CONFIG[fieldId].placeholder}: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const renderActivitySidebarField = (
    fieldId: ActivityEditableFieldId,
    label: string,
    Icon: React.ComponentType<{ size?: number; className?: string }>,
    options: { showAll: boolean; showInfo?: boolean } = { showAll: false }
  ) => {
    const config = ACTIVITY_EDITABLE_FIELD_CONFIG[fieldId];
    const rawValue = getActivityFieldValue(fieldId);
    const displayValue = fieldId === 'priority'
      ? getActivityPriorityLabel(rawValue)
      : normalizeActivitySidebarValue(rawValue);
    const isEditing = activitySidebarEditingFieldId === fieldId;
    const isHovered = activitySidebarHoveredField === fieldId;
    const shouldRender =
      fieldId === 'owner'
      || fieldId === 'visibility'
      || fieldId === 'priority'
      || fieldId === 'contactStage'
      || fieldId === 'accountStage'
      || options.showAll
      || Boolean(displayValue)
      || isEditing;
    if (!shouldRender) return null;

    const showAddPrompt = isHovered && !displayValue && !isEditing;
    const inputType = config.inputType === 'date'
      ? 'date'
      : fieldId.toLowerCase().includes('email')
        ? 'email'
        : fieldId.toLowerCase().includes('phone')
          ? 'tel'
          : 'text';
    const detailsTarget = getActivityDetailsTarget();
    const businessUnitOptions = Array.from(new Set(
      ['In-Store', String((detailsTarget?.record as any)?.businessUnit || '').trim()].filter(Boolean)
    ));
    const stageOptions = fieldId === 'accountStage'
      ? accountStageOptions
      : fieldId === 'contactStage'
        ? contactStageOptions
        : [];

    return (
      <div
        className="mb-2"
        style={{
          cursor: 'pointer',
          padding: '0.5rem',
          borderRadius: '4px',
          backgroundColor: isHovered && !isEditing ? '#f8f9fa' : 'transparent',
          transition: 'background-color 0.2s',
        }}
        onMouseEnter={() => setActivitySidebarHoveredField(fieldId)}
        onMouseLeave={() => setActivitySidebarHoveredField(null)}
        onClick={() => startEditingSidebarField(fieldId)}
      >
        <div className="d-flex align-items-center gap-2">
          <span style={activitySidebarIconStyle}>
            <Icon size={14} />
          </span>
          <span
            style={{
              fontSize: '0.75rem',
              color: showAddPrompt ? '#0d6efd' : (displayValue ? '#212529' : '#6c757d'),
              fontWeight: showAddPrompt ? 500 : 'normal',
            }}
          >
            {displayValue || (showAddPrompt ? '+ Add value' : label)}
          </span>
          {options.showInfo && <InfoCircle size={9} className="ms-1 text-muted" />}
        </div>
        {isEditing && (
          <div className="d-flex align-items-center gap-2 mt-2" onClick={(event) => event.stopPropagation()}>
            {fieldId === 'owner' ? (
              <Form.Select
                value={activitySidebarFieldDraftValue || (selectedMeetingVisibilityValue === 'global' ? DEFAULT_OWNER_FILTER_LABEL : selfOwnerBaseLabel)}
                onChange={(event) => setActivitySidebarFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                {detailOwnerOptions.map((option) => (
                  <option key={`activity-owner-option-${option}`} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            ) : fieldId === 'visibility' ? (
              <Form.Select
                value={activitySidebarFieldDraftValue || selectedMeetingVisibilityValue}
                onChange={(event) => setActivitySidebarFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="global">Global (visible for everyone)</option>
                <option value="personal">Personal (private)</option>
              </Form.Select>
            ) : fieldId === 'accountStage' || fieldId === 'contactStage' ? (
              <Form.Select
                value={activitySidebarFieldDraftValue}
                onChange={(event) => setActivitySidebarFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="">Select</option>
                {stageOptions.map((option) => (
                  <option key={`${fieldId}-option-${option}`} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            ) : fieldId === 'priority' ? (
              <Form.Select
                value={activitySidebarFieldDraftValue || 'normal'}
                onChange={(event) => setActivitySidebarFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                {ACTIVITY_PRIORITY_OPTIONS.map((option) => (
                  <option key={`activity-priority-${option.value}`} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            ) : fieldId === 'businessUnit' ? (
              <Form.Select
                value={activitySidebarFieldDraftValue}
                onChange={(event) => setActivitySidebarFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              >
                <option value="">Select business unit</option>
                {businessUnitOptions.map((option) => (
                  <option key={`activity-business-unit-${option}`} value={option}>
                    {option}
                  </option>
                ))}
              </Form.Select>
            ) : fieldId === 'sourceOrigin' ? (
              <>
                <Form.Select
                  value={activitySidebarFieldDraftValue}
                  onChange={(event) => setActivitySidebarFieldDraftValue(event.target.value)}
                  style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                  autoFocus
                >
                  <option value="">(None)</option>
                  {SOURCE_ORIGIN_OPTIONS.map((option) => (
                    <option key={`activity-source-origin-${option}`} value={option}>
                      {option}
                    </option>
                  ))}
                </Form.Select>
                <OverlayTrigger
                  trigger="click"
                  rootClose
                  placement="right"
                  overlay={(
                    <Popover id="activity-source-origin-info-popover" style={{ maxWidth: 360 }}>
                      <Popover.Header as="div" style={{ fontWeight: 600 }}>
                        Source details
                      </Popover.Header>
                      <Popover.Body style={{ fontSize: '0.78rem', lineHeight: 1.35 }}>
                        <div className="mb-2">
                          Source indicates the origin of the contact. You can track source performance using Insights reports.
                        </div>
                        <div style={{ color: '#0d6efd', fontWeight: 500 }}>View report</div>
                        <div style={{ color: '#0d6efd', fontWeight: 500 }}>Learn more about sources</div>
                      </Popover.Body>
                    </Popover>
                  )}
                >
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0"
                    onClick={(e) => e.stopPropagation()}
                    style={{ color: '#0d6efd', textDecoration: 'none' }}
                    aria-label="Source origin help"
                  >
                    <InfoCircle size={14} />
                  </Button>
                </OverlayTrigger>
              </>
            ) : (
              <Form.Control
                type={inputType}
                value={activitySidebarFieldDraftValue}
                placeholder={label}
                onChange={(event) => setActivitySidebarFieldDraftValue(event.target.value)}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                autoFocus
              />
            )}
            {fieldId !== 'owner' && fieldId !== 'visibility' && (
              <Button
                variant="outline-danger"
                size="sm"
                onClick={(event) => {
                  event.stopPropagation();
                  setActivitySidebarFieldDraftValue('');
                }}
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
              >
                Clear
              </Button>
            )}
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                cancelEditingSidebarField();
              }}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                handleSaveSidebarField(fieldId);
              }}
              disabled={isUpdatingMeeting || isUpdatingLead || isUpdatingClient || isUpdatingContact}
              style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', whiteSpace: 'nowrap' }}
            >
              {isUpdatingMeeting || isUpdatingLead || isUpdatingClient || isUpdatingContact ? (
                <Spinner size="sm" animation="border" />
              ) : 'Save'}
            </Button>
          </div>
        )}
      </div>
    );
  };

  const renderActivityLabelsRow = (showAll: boolean) => {
    if (!selectedMeeting) return null;

    const isOpen = showActivityLabelsDropdown;
    const hasLabels = selectedActivityLabels.length > 0;
    const shouldRender = showAll || hasLabels || isOpen;
    if (!shouldRender) return null;

    const filteredLabelOptions = allAvailableLabels.filter((label) =>
      label.toLowerCase().includes(activityLabelSearchQuery.toLowerCase())
    );

    return (
      <Dropdown
        show={isOpen}
        onToggle={(nextIsOpen) => {
          setShowActivityLabelsDropdown(nextIsOpen);
          if (!nextIsOpen) setActivityLabelSearchQuery('');
        }}
        className="mb-2"
        style={{ marginBottom: 0 }}
      >
        <Dropdown.Toggle
          as="div"
          className="text-decoration-none labels-dropdown-toggle"
          style={{
            width: '100%',
            padding: '0.5rem',
            borderRadius: '4px',
            backgroundColor: isHoveringActivityLabels ? '#f8f9fa' : 'transparent',
            transition: 'background-color 0.2s',
            cursor: 'pointer',
            minHeight: '32px',
            border: 'none',
            textAlign: 'left'
          }}
          onMouseEnter={() => setIsHoveringActivityLabels(true)}
          onMouseLeave={() => setIsHoveringActivityLabels(false)}
        >
          <div className="d-flex align-items-center gap-2">
            <span style={activitySidebarIconStyle}>
              <Tag size={14} />
            </span>
            {hasLabels ? (
              <div className="d-flex flex-wrap gap-1 align-items-center">
                {selectedActivityLabels.map((label) => (
                  <Badge key={`activity-label-${label}`} bg={getLabelVariant(label)} style={{ fontSize: '0.55rem', padding: '0.15rem 0.35rem' }}>
                    {label}
                  </Badge>
                ))}
              </div>
            ) : (
              <span
                style={{
                  fontSize: '0.75rem',
                  color: isHoveringActivityLabels ? '#0d6efd' : '#6c757d',
                  fontWeight: isHoveringActivityLabels ? 500 : 'normal'
                }}
              >
                {isHoveringActivityLabels ? '+ Add Labels' : 'No labels'}
              </span>
            )}
          </div>
        </Dropdown.Toggle>
        <Dropdown.Menu
          show={isOpen}
          style={{
            minWidth: '250px',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '0.5rem',
            marginTop: '0.25rem'
          }}
        >
          <div className="mb-2">
            <InputGroup size="sm">
              <InputGroup.Text>
                <Search size={14} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Search label"
                value={activityLabelSearchQuery}
                onChange={(event) => setActivityLabelSearchQuery(event.target.value)}
                onClick={(event) => event.stopPropagation()}
                style={{ fontSize: '0.75rem' }}
              />
            </InputGroup>
          </div>
          {filteredLabelOptions.map((label) => {
            const isAssigned = selectedActivityLabels.includes(label);
            return (
              <Dropdown.Item
                key={`activity-label-option-${label}`}
                active={isAssigned}
                onClick={async (event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  await toggleActivityLabel(label);
                }}
                style={{ fontSize: '0.75rem' }}
              >
                <div className="d-flex align-items-center">
                  {isAssigned && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                  <Badge bg={getLabelVariant(label)} style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}>
                    {label}
                  </Badge>
                </div>
              </Dropdown.Item>
            );
          })}
          <Dropdown.Divider />
          <Dropdown.Item
            onClick={async (event) => {
              event.preventDefault();
              event.stopPropagation();
              await handleCreateNewActivityLabel();
            }}
            style={{ fontSize: '0.75rem', color: '#0d6efd' }}
          >
            <div className="d-flex align-items-center">
              <Plus className="me-2" size={14} />
              <span>Add new label</span>
            </div>
          </Dropdown.Item>
        </Dropdown.Menu>
      </Dropdown>
    );
  };

  const getMeetingDateTimeLabel = (isoDateTime?: string): string => {
    if (!isoDateTime) return '—';
    return `${toLocaleDateStringInTimezone(
      isoDateTime,
      'en-US',
      { month: 'short', day: 'numeric', year: 'numeric' },
      tenantTimezone
    )} ${toLocaleTimeStringInTimezone(
      isoDateTime,
      'en-US',
      { hour: 'numeric', minute: '2-digit' },
      tenantTimezone
    )}`;
  };

  const formatDate = (isoDateTime?: string): string => {
    if (!isoDateTime) return '—';
    return `${toLocaleDateStringInTimezone(
      isoDateTime,
      'en-US',
      { month: 'short', day: 'numeric', year: 'numeric' },
      tenantTimezone
    )} ${toLocaleTimeStringInTimezone(
      isoDateTime,
      'en-US',
      { hour: 'numeric', minute: '2-digit' },
      tenantTimezone
    )}`;
  };

  const getTypeLabel = (meeting: Meeting): string => {
    const type = String(meeting.meetingType || '').toLowerCase();
    return MEETING_TYPE_LABELS[type] || (meeting.title || 'Activity');
  };

  const TypeIcon = ({ meeting }: { meeting: Meeting }) => {
    const type = String(meeting.meetingType || '').toLowerCase();
    const Icon = MEETING_TYPE_ICONS[type] || Clock;
    return <span className="me-1 text-secondary"><Icon size={14} /></span>;
  };

  const handleCalendarPreviousMonth = () => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1, 12, 0, 0, 0));
  };

  const handleCalendarNextMonth = () => {
    setCalendarMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1, 12, 0, 0, 0));
  };

  const handleToggleMeetingDone = async (meeting: Meeting, nextDone: boolean) => {
    try {
      const nextTags = upsertMeetingMetaTag(
        Array.isArray(meeting.tags) ? meeting.tags : [],
        'meta_done:',
        nextDone ? '1' : '0'
      );
      await updateMeeting({ meetingId: meeting.id, markAsDone: nextDone, tags: nextTags }).unwrap();
      await refetchMeetings();
      setSelectedMeeting((prev) => (prev?.id === meeting.id ? { ...prev, markAsDone: nextDone, tags: nextTags } : prev));
    } catch (error: any) {
      console.error('Error marking activity done:', error);
      alert(`Failed to update activity: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleOpenActivityDetail = (meeting: Meeting) => {
    setSelectedMeeting(meeting);
    setActivitySidebarTab('notes');
    setShowActivitySidebar(true);
  };

  const populateActivityEditorFromMeeting = (meeting: Meeting) => {
    const { typeId, form } = meetingToActivityForm(meeting);
    setSelectedActivityType(typeId);
    setActivityForm(form);
    setEditingMeetingId(meeting.id);
    setDealLeadSearch('');
    setPeopleSearch('');
    setOrgSearch('');
    setShowDealLeadDropdown(false);
    setShowPeopleDropdown(false);
    setShowOrgDropdown(false);
  };

  const handleCloseActivitySidebar = () => {
    setShowActivitySidebar(false);
    setActivitySidebarTab('notes');
    setShowActivityLabelsDropdown(false);
    setActivityLabelSearchQuery('');
    setSelectedMeeting(null);
  };

  const handleEditActivityFromSidebar = () => {
    if (!selectedMeeting) return;
    populateActivityEditorFromMeeting(selectedMeeting);
    setActivitySidebarTab('activity');
  };

  useEffect(() => {
    if (!selectedMeeting || showScheduleModal) return;
    populateActivityEditorFromMeeting(selectedMeeting);
  }, [selectedMeeting?.id, showScheduleModal]);

  const handleDeleteActivityFromSidebar = async () => {
    if (!selectedMeeting) return;
    const confirmed = window.confirm('Delete this activity? This cannot be undone.');
    if (!confirmed) return;
    try {
      await deleteMeeting(selectedMeeting.id).unwrap();
      handleCloseActivitySidebar();
      await refetchMeetings();
    } catch (error: any) {
      console.error('Error deleting activity:', error);
      alert(`Failed to delete activity: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleSaveSidebarNotes = async () => {
    if (!selectedMeeting) return;
    try {
      const updated = await updateMeeting({
        meetingId: selectedMeeting.id,
        notes: String(activitySidebarNoteDraft || '').trim(),
      }).unwrap();
      const nextMeeting = (updated as any)?.data?.meeting || (updated as any)?.meeting || null;
      if (nextMeeting) {
        setSelectedMeeting(nextMeeting as Meeting);
      } else {
        setSelectedMeeting((prev) => (prev ? { ...prev, notes: String(activitySidebarNoteDraft || '').trim() } : prev));
      }
      await refetchMeetings();
    } catch (error: any) {
      console.error('Error saving activity notes:', error);
      alert(`Failed to save notes: ${error?.data?.message || error?.message || 'Unknown error'}`);
    }
  };

  const handleOpenScheduleModal = () => {
    setEditingMeetingId(null);
    const defaultType: ActivityTypeId = 'call';
    setSelectedActivityType(defaultType);
    setActivityForm(createDefaultActivityForm(defaultType));
    setDealLeadSearch('');
    setPeopleSearch('');
    setOrgSearch('');
    setShowDealLeadDropdown(false);
    setShowPeopleDropdown(false);
    setShowOrgDropdown(false);
    setShowScheduleModal(true);
  };

  const handleCloseScheduleModal = () => {
    setShowScheduleModal(false);
    setEditingMeetingId(null);
  };

  const handleSelectActivityType = (activityType: ActivityTypeId) => {
    const option = getActivityTypeOption(activityType);
    setSelectedActivityType(activityType);
    setActivityForm((prev) => ({
      ...prev,
      title: option.label,
      meetingType: prev.videoCallProvider ? 'video' : option.meetingType,
    }));
  };

  const handleActivityFieldChange = (field: keyof ActivityFormData, value: string | boolean) => {
    setActivityForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveActivity = async () => {
    const title = String(activityForm.title || '').trim();
    if (!title) {
      alert('Activity title is required.');
      return;
    }
    if (!activityForm.startTime || !activityForm.endTime) {
      alert('Start and end time are required.');
      return;
    }

    const startDate = new Date(activityForm.startTime);
    if (Number.isNaN(startDate.getTime())) {
      alert('Start time is invalid.');
      return;
    }

    let endDate = new Date(activityForm.endTime);
    if (Number.isNaN(endDate.getTime()) || endDate <= startDate) {
      endDate = new Date(startDate.getTime() + 60 * 60 * 1000);
    }

    const selectedClient = activityForm.clientId
      ? clients.find((item) => item.id === activityForm.clientId)
      : null;
    const selectedAccount = activityForm.accountId
      ? accounts.find((item) => item.id === activityForm.accountId)
      : null;
    const selectedContact = activityForm.contactId
      ? contacts.find((item) => item.id === activityForm.contactId)
      : null;
    const selectedDeal = activityForm.dealId
      ? deals.find((item) => item.id === activityForm.dealId)
      : null;
    const selectedLead = activityForm.leadId
      ? leads.find((item) => item.id === activityForm.leadId)
      : null;

    const normalizedOrganizationName = activityForm.accountId
      ? String(selectedAccount?.name || '').trim()
      : String(activityForm.organizationName || orgSearch || '').trim();

    const normalizedContactName = activityForm.contactId
      ? `${selectedContact?.firstName || ''} ${selectedContact?.lastName || ''}`.trim()
      : String(activityForm.contactName || peopleSearch || '').trim();

    const normalizedContactEmail = activityForm.contactId
      ? String(selectedContact?.email || '').trim()
      : String(activityForm.contactEmail || '').trim();

    const normalizedDealLeadLabel = activityForm.dealId
      ? String(selectedDeal?.name || '').trim()
      : activityForm.leadId
        ? String(
          selectedLead
            ? (selectedLead.organization
              ? `${selectedLead.name || ''} (${selectedLead.organization})`
              : (selectedLead.name || ''))
            : ''
        ).trim()
        : String(activityForm.dealLeadLabel || dealLeadSearch || '').trim();

    const metadataTags = [
      `meta_org:${encodeURIComponent(normalizedOrganizationName || '')}`,
      `meta_contact_name:${encodeURIComponent(normalizedContactName || '')}`,
      `meta_contact_email:${encodeURIComponent(normalizedContactEmail || '')}`,
      `meta_deal_lead:${encodeURIComponent(normalizedDealLeadLabel || '')}`,
      `meta_priority:${encodeURIComponent(activityForm.priority || 'normal')}`,
      `meta_free_busy:${encodeURIComponent(activityForm.freeBusy || 'free')}`,
      `meta_done:${activityForm.markAsDone ? '1' : '0'}`,
    ];
    const activityTag = `activity:${selectedActivityType}`;
    const purposeTag = `purpose:${activityForm.purposeTypeId || DEFAULT_ACTIVITY_PURPOSE_ID}`;
    const tags = Array.from(new Set([activityTag, purposeTag, ...metadataTags]));

    const payload = {
      title,
      description: activityForm.description || undefined,
      startTime: startDate.toISOString(),
      endTime: endDate.toISOString(),
      location: activityForm.location || undefined,
      meetingType: activityForm.videoCallProvider ? 'video' : activityForm.meetingType,
      meetingUrl: activityForm.meetingUrl || undefined,
      accountId: activityForm.accountId || undefined,
      clientId: activityForm.clientId || undefined,
      contactId: activityForm.contactId || undefined,
      dealId: activityForm.dealId || undefined,
      leadId: activityForm.leadId || undefined,
      organizationName: normalizedOrganizationName || undefined,
      contactName: normalizedContactName || undefined,
      contactEmail: normalizedContactEmail || undefined,
      dealLeadLabel: normalizedDealLeadLabel || undefined,
      purposeTypeId: activityForm.purposeTypeId || DEFAULT_ACTIVITY_PURPOSE_ID,
      priority: activityForm.priority,
      freeBusy: activityForm.freeBusy,
      markAsDone: Boolean(activityForm.markAsDone),
      notes: activityForm.notes || undefined,
      tags,
      visibility: 'personal' as const,
    };

    try {
      let savedMeeting: Meeting | null = null;
      if (editingMeetingId) {
        const updated = await updateMeeting({ meetingId: editingMeetingId, ...payload }).unwrap();
        savedMeeting = ((updated as any)?.data?.meeting || (updated as any)?.meeting || null) as Meeting | null;
      } else {
        const created = await createMeeting(payload).unwrap();
        savedMeeting = ((created as any)?.data?.meeting || (created as any)?.meeting || null) as Meeting | null;
      }

      await refetchMeetings();
      if (editingMeetingId) {
        if (savedMeeting) {
          setSelectedMeeting(savedMeeting);
        } else {
          setSelectedMeeting((prev) => (prev ? {
            ...prev,
            ...payload,
            id: prev.id,
            startTime: payload.startTime,
            endTime: payload.endTime,
          } as Meeting : prev));
        }
        if (showScheduleModal) {
          setShowScheduleModal(false);
        }
        return;
      }
      setShowScheduleModal(false);
      setEditingMeetingId(null);
      setActivityForm(createDefaultActivityForm('call'));
    } catch (error: any) {
      console.error(editingMeetingId ? 'Error updating activity:' : 'Error creating activity:', error);
      alert(
        editingMeetingId
          ? `Failed to update activity: ${error?.data?.message || error?.message || 'Unknown error'}`
          : `Failed to create activity: ${error?.data?.message || error?.message || 'Unknown error'}`
      );
    }
  };

  const renderActivityEditor = (embedded = false) => (
    <>
      <Form.Control
        size="sm"
        value={activityForm.title}
        onChange={(e) => handleActivityFieldChange('title', e.target.value)}
        style={{ fontSize: '1rem', padding: '0.4rem 0.5rem', marginBottom: '0.5rem' }}
        placeholder="Activity title"
      />

      <div className="d-flex gap-1 flex-wrap mb-3">
        {ACTIVITY_TYPE_OPTIONS.map((option) => {
          const Icon = option.icon;
          return (
            <button
              key={option.id}
              type="button"
              onClick={() => handleSelectActivityType(option.id)}
              style={{
                width: 36,
                height: 36,
                borderRadius: 6,
                border: '1px solid #dee2e6',
                backgroundColor: selectedActivityType === option.id ? '#0d6efd' : '#fff',
                color: selectedActivityType === option.id ? '#fff' : '#6c757d',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
              }}
              title={option.label}
            >
              <Icon size={16} />
            </button>
          );
        })}
      </div>

      <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
        <Clock size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
        <Form.Control
          type="date"
          size="sm"
          value={activityForm.startTime ? activityForm.startTime.slice(0, 10) : ''}
          onChange={(e) => {
            const date = e.target.value;
            const startTime = activityForm.startTime ? activityForm.startTime.slice(11, 16) : '12:00';
            const endTime = activityForm.endTime ? activityForm.endTime.slice(11, 16) : '13:00';
            setActivityForm((prev) => ({
              ...prev,
              startTime: `${date}T${startTime}`,
              endTime: `${date}T${endTime}`,
            }));
          }}
          style={{ width: 140, fontSize: '0.75rem' }}
        />
        <Form.Control
          type="time"
          size="sm"
          value={activityForm.startTime ? activityForm.startTime.slice(11, 16) : ''}
          onChange={(e) => {
            const date = activityForm.startTime ? activityForm.startTime.slice(0, 10) : getDateKeyInTimezone(new Date(), tenantTimezone);
            handleActivityFieldChange('startTime', `${date}T${e.target.value}`);
          }}
          style={{ width: 100, fontSize: '0.75rem' }}
        />
        <span style={{ color: '#6c757d' }}>-</span>
        <Form.Control
          type="time"
          size="sm"
          value={activityForm.endTime ? activityForm.endTime.slice(11, 16) : ''}
          onChange={(e) => {
            const date = activityForm.endTime ? activityForm.endTime.slice(0, 10) : activityForm.startTime.slice(0, 10);
            handleActivityFieldChange('endTime', `${date}T${e.target.value}`);
          }}
          style={{ width: 100, fontSize: '0.75rem' }}
        />
      </div>

      <div className="d-flex align-items-center gap-2 mb-2">
        <Flag size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
        <Form.Select
          size="sm"
          value={activityForm.priority}
          onChange={(e) => handleActivityFieldChange('priority', e.target.value)}
          style={{ width: 120, fontSize: '0.75rem' }}
        >
          <option value="low">Low</option>
          <option value="normal">Normal</option>
          <option value="high">High</option>
        </Form.Select>
      </div>

      <div className="d-flex align-items-center gap-2 mb-2">
        <Briefcase size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
        <Form.Select
          size="sm"
          value={activityForm.purposeTypeId}
          onChange={(e) => handleActivityFieldChange('purposeTypeId', e.target.value)}
          style={{ flex: 1, maxWidth: 280, fontSize: '0.75rem' }}
        >
          {DEFAULT_ACTIVITY_PURPOSE_OPTIONS.map((option) => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </Form.Select>
      </div>

      <div className="d-flex align-items-center gap-2 mb-2">
        <ChatDots size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
        <Form.Control
          size="sm"
          value={activityForm.location}
          onChange={(e) => handleActivityFieldChange('location', e.target.value)}
          placeholder="Add location"
          style={{ flex: 1, fontSize: '0.75rem' }}
        />
      </div>

      <div className="d-flex align-items-center gap-2 mb-3">
        <Camera size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
        <Form.Select
          size="sm"
          value={activityForm.videoCallProvider}
          onChange={(e) => {
            const provider = e.target.value as '' | 'zoom' | 'teams' | 'google-meet';
            setActivityForm((prev) => ({
              ...prev,
              videoCallProvider: provider,
              meetingType: provider ? 'video' : getActivityTypeOption(selectedActivityType).meetingType,
              meetingUrl: provider ? prev.meetingUrl : '',
            }));
          }}
          style={{ flex: 1, maxWidth: 260, fontSize: '0.75rem' }}
        >
          <option value="">Install video call integration</option>
          <option value="zoom">Zoom meeting</option>
          <option value="teams">Microsoft Teams meeting</option>
          <option value="google-meet">Google Meet meeting</option>
        </Form.Select>
      </div>

      {activityForm.videoCallProvider && (
        <div className="d-flex align-items-center gap-2 mb-3" style={{ marginLeft: 24 }}>
          <Form.Control
            size="sm"
            value={activityForm.meetingUrl}
            onChange={(e) => handleActivityFieldChange('meetingUrl', e.target.value)}
            placeholder={
              activityForm.videoCallProvider === 'google-meet'
                ? 'Paste Google Meet link here'
                : activityForm.videoCallProvider === 'zoom'
                  ? 'Paste Zoom link here'
                  : 'Paste Teams link here'
            }
            style={{ flex: 1, fontSize: '0.75rem' }}
          />
        </div>
      )}

      <div className="d-flex align-items-center gap-2 mb-3">
        <Tag size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
        <Form.Select
          size="sm"
          value={activityForm.freeBusy}
          onChange={(e) => handleActivityFieldChange('freeBusy', e.target.value)}
          style={{ width: 100, fontSize: '0.75rem' }}
        >
          <option value="free">Free</option>
          <option value="busy">Busy</option>
        </Form.Select>
        <OverlayTrigger
          trigger="click"
          placement="top"
          rootClose
          overlay={(
            <Popover id="activity-free-busy-popover" style={{ maxWidth: 320 }}>
              <Popover.Header as="h3" className="fw-bold" style={{ fontSize: '0.9rem' }}>
                Free/Busy
              </Popover.Header>
              <Popover.Body style={{ fontSize: '0.8rem' }}>
                This option controls whether you are shown as available or unavailable for other meetings at the same time.
              </Popover.Body>
            </Popover>
          )}
        >
          <span style={{ color: '#6c757d', cursor: 'pointer' }} role="button" tabIndex={0}>
            <InfoCircle size={16} />
          </span>
        </OverlayTrigger>
      </div>

      <div className="mb-3">
        <div className="d-flex align-items-center gap-2 mb-1">
          <Sticky size={16} style={{ color: '#6c757d' }} />
          <span style={{ fontSize: '0.75rem', fontWeight: 500 }}>Notes</span>
        </div>
        <Form.Control
          as="textarea"
          rows={4}
          size="sm"
          value={activityForm.notes}
          onChange={(e) => handleActivityFieldChange('notes', e.target.value)}
          placeholder="Notes are visible within the app, but not to event guests."
          style={{ fontSize: '0.75rem', padding: '0.4rem', backgroundColor: '#fffef0', border: '1px solid #dee2e6' }}
        />
        <div style={{ fontSize: '0.65rem', color: '#6c757d', marginTop: '0.25rem' }}>
          Notes are visible within the app, but not to event guests.
        </div>
      </div>

      <div className="mb-2" style={{ width: '100%' }}>
        <div className="d-flex align-items-center gap-2 mb-1">
          <Person size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
          <Form.Control size="sm" value="You" readOnly style={{ flex: 1, fontSize: '0.75rem', backgroundColor: '#f8f9fa' }} />
        </div>

        <div className="d-flex align-items-center gap-2 mb-1" style={{ position: 'relative' }}>
          <CurrencyDollar size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
          <div style={{ flex: 1, position: 'relative' }}>
            <Form.Control
              size="sm"
              value={showDealLeadDropdown ? (dealLeadSearch || dealLeadDisplayLabel) : dealLeadDisplayLabel}
              onChange={(e) => {
                const nextValue = e.target.value;
                setDealLeadSearch(nextValue);
                setShowDealLeadDropdown(true);
                setActivityForm((prev) => ({
                  ...(prev.dealId || prev.leadId
                    ? prev
                    : {
                        ...prev,
                        dealLeadLabel: nextValue,
                      }),
                }));
              }}
              onFocus={() => setShowDealLeadDropdown(true)}
              onBlur={() => {
                const hasLinkedDealOrLead = Boolean(activityForm.dealId || activityForm.leadId);
                setTimeout(() => {
                  setShowDealLeadDropdown(false);
                  if (hasLinkedDealOrLead) setDealLeadSearch('');
                }, 200);
              }}
              placeholder="Deal or Lead"
              style={{ width: '100%', fontSize: '0.75rem' }}
            />
            {(activityForm.dealId || activityForm.leadId || String(activityForm.dealLeadLabel || '').trim().length > 0) && (
              <button
                type="button"
                onClick={() => {
                  setDealLeadSearch('');
                  setActivityForm((prev) => ({
                    ...prev,
                    dealId: '',
                    leadId: '',
                    dealLeadLabel: '',
                  }));
                }}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: '#6c757d',
                }}
              >
                <XLg size={12} />
              </button>
            )}
            {showDealLeadDropdown && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  backgroundColor: '#fff',
                  border: '1px solid #dee2e6',
                  borderRadius: 4,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                {filteredDealsAndLeads.length === 0 ? (
                  <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No deals or leads found</div>
                ) : (
                  filteredDealsAndLeads.map((item) => (
                    <div
                      key={`${item.type}-${item.id}`}
                      onClick={() => {
                        if (item.type === 'deal') {
                          setActivityForm((prev) => ({ ...prev, dealId: item.id, leadId: '', dealLeadLabel: '' }));
                        } else {
                          setActivityForm((prev) => ({ ...prev, leadId: item.id, dealId: '', dealLeadLabel: '' }));
                        }
                        setDealLeadSearch('');
                        setShowDealLeadDropdown(false);
                      }}
                      style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {item.label}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 mb-1" style={{ position: 'relative' }}>
          <People size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
          <div style={{ flex: 1, position: 'relative' }}>
            <Form.Control
              size="sm"
              value={showPeopleDropdown ? (peopleSearch || contactDisplayLabel) : contactDisplayLabel}
              onChange={(e) => {
                const nextValue = e.target.value;
                setPeopleSearch(nextValue);
                setShowPeopleDropdown(true);
                setActivityForm((prev) => ({
                  ...(prev.contactId || prev.clientId || prev.leadId
                    ? prev
                    : {
                        ...prev,
                        contactName: nextValue,
                      }),
                }));
              }}
              onFocus={() => setShowPeopleDropdown(true)}
              onBlur={() => {
                const hasLinkedPerson = Boolean(activityForm.contactId || activityForm.clientId || activityForm.leadId);
                setTimeout(() => {
                  setShowPeopleDropdown(false);
                  if (hasLinkedPerson) setPeopleSearch('');
                }, 200);
              }}
              placeholder="People"
              style={{ width: '100%', fontSize: '0.75rem' }}
            />
            {(activityForm.contactId || activityForm.clientId || activityForm.leadId || String(activityForm.contactName || '').trim().length > 0) && (
              <button
                type="button"
                onClick={() => {
                  setPeopleSearch('');
                  setActivityForm((prev) => ({
                    ...prev,
                    clientId: '',
                    contactId: '',
                    leadId: '',
                    contactName: '',
                    contactEmail: '',
                  }));
                }}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: '#6c757d',
                }}
              >
                <XLg size={12} />
              </button>
            )}
            {showPeopleDropdown && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  backgroundColor: '#fff',
                  border: '1px solid #dee2e6',
                  borderRadius: 4,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                {filteredPeople.length === 0 ? (
                  <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No people found</div>
                ) : (
                  filteredPeople.map((item) => (
                    <div
                      key={`${item.type}-${item.id}`}
                      onClick={() => {
                        if (item.type === 'contact') {
                          const selectedContact = contacts.find((contact) => contact.id === item.id);
                          const selectedContactName = selectedContact
                            ? `${selectedContact.firstName || ''} ${selectedContact.lastName || ''}`.trim()
                            : item.label;
                          setActivityForm((prev) => ({
                            ...prev,
                            contactId: item.id,
                            contactName: selectedContactName,
                            contactEmail: String(selectedContact?.email || '').trim(),
                          }));
                        } else if (item.type === 'client') {
                          const selectedClient = clients.find((client) => client.id === item.id);
                          const selectedClientName = String(selectedClient?.name || '').trim() || item.label;
                          setActivityForm((prev) => ({
                            ...prev,
                            clientId: item.id,
                            contactId: '',
                            leadId: '',
                            contactName: selectedClientName,
                            contactEmail: String(selectedClient?.email || '').trim(),
                            organizationName: String(selectedClient?.organization || '').trim() || prev.organizationName,
                          }));
                        } else {
                          const selectedLead = leads.find((lead) => lead.id === item.id);
                          const selectedLeadName = String(selectedLead?.name || '').trim() || item.label;
                          setActivityForm((prev) => ({
                            ...prev,
                            leadId: item.id,
                            clientId: '',
                            contactId: '',
                            contactName: selectedLeadName,
                            contactEmail: String(selectedLead?.email || '').trim(),
                            organizationName: String(selectedLead?.organization || '').trim() || prev.organizationName,
                          }));
                        }
                        setPeopleSearch('');
                        setShowPeopleDropdown(false);
                      }}
                      style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {item.label}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="d-flex align-items-center gap-2 mb-1">
          <Envelope size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
          <Form.Control
            size="sm"
            value={activityForm.contactEmail}
            onChange={(e) => setActivityForm((prev) => ({ ...prev, contactEmail: e.target.value }))}
            placeholder="Contact email"
            style={{ width: '100%', fontSize: '0.75rem' }}
          />
        </div>

        <div className="d-flex align-items-center gap-2" style={{ position: 'relative' }}>
          <Building size={16} style={{ color: '#6c757d', flexShrink: 0 }} />
          <div style={{ flex: 1, position: 'relative' }}>
            <Form.Control
              size="sm"
              value={showOrgDropdown ? (orgSearch || organizationDisplayLabel) : organizationDisplayLabel}
              onChange={(e) => {
                const nextValue = e.target.value;
                setOrgSearch(nextValue);
                setShowOrgDropdown(true);
                setActivityForm((prev) => ({
                  ...(prev.accountId
                    ? prev
                    : {
                        ...prev,
                        organizationName: nextValue,
                      }),
                }));
              }}
              onFocus={() => setShowOrgDropdown(true)}
              onBlur={() => {
                const hasLinkedAccount = Boolean(activityForm.accountId);
                setTimeout(() => {
                  setShowOrgDropdown(false);
                  if (hasLinkedAccount) setOrgSearch('');
                }, 200);
              }}
              placeholder="Organization"
              style={{ width: '100%', fontSize: '0.75rem' }}
            />
            {(activityForm.accountId || String(activityForm.organizationName || '').trim().length > 0) && (
              <button
                type="button"
                onClick={() => {
                  setOrgSearch('');
                  setActivityForm((prev) => ({
                    ...prev,
                    accountId: '',
                    organizationName: '',
                  }));
                }}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  color: '#6c757d',
                }}
              >
                <XLg size={12} />
              </button>
            )}
            {showOrgDropdown && (
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: 0,
                  right: 0,
                  zIndex: 10,
                  backgroundColor: '#fff',
                  border: '1px solid #dee2e6',
                  borderRadius: 4,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  maxHeight: 200,
                  overflowY: 'auto',
                }}
              >
                {filteredOrganizations.length === 0 ? (
                  <div style={{ padding: '0.5rem 0.75rem', fontSize: '0.75rem', color: '#6c757d' }}>No organizations found</div>
                ) : (
                  filteredOrganizations.map((item) => (
                    <div
                      key={`${item.type}-${item.id}`}
                      onClick={() => {
                        if (item.type === 'account') {
                          setActivityForm((prev) => ({ ...prev, accountId: item.id, organizationName: item.label }));
                        } else {
                          setActivityForm((prev) => ({ ...prev, accountId: '', organizationName: item.label }));
                        }
                        setOrgSearch('');
                        setShowOrgDropdown(false);
                      }}
                      style={{ padding: '0.4rem 0.75rem', fontSize: '0.75rem', cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      {item.label}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {embedded ? (
        <div className="d-flex justify-content-between align-items-center mt-3 pt-3 border-top">
          <Form.Check
            type="checkbox"
            id="activity-sidebar-markAsDone"
            label="Mark as done"
            checked={activityForm.markAsDone}
            onChange={(e) => handleActivityFieldChange('markAsDone', e.target.checked)}
            style={{ fontSize: '0.75rem', marginBottom: 0 }}
          />
          <div className="d-flex gap-2">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => selectedMeeting && populateActivityEditorFromMeeting(selectedMeeting)}
              disabled={!selectedMeeting || isUpdatingMeeting}
            >
              Reset
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSaveActivity}
              disabled={
                isCreatingMeeting
                || isUpdatingMeeting
                || !activityForm.title
                || !activityForm.startTime
                || !activityForm.endTime
              }
            >
              {isCreatingMeeting || isUpdatingMeeting ? <Spinner size="sm" animation="border" /> : 'Save'}
            </Button>
          </div>
        </div>
      ) : null}
    </>
  );

  if (meetingsError) {
    return (
      <Container fluid className="py-4">
        <div className="text-danger">Failed to load activities.</div>
      </Container>
    );
  }

  return (
    <Container fluid className="p-0" style={{ backgroundColor: '#ffffff', minHeight: '100vh' }}>
      <div style={{ backgroundColor: '#ffffff', paddingTop: '1rem', paddingBottom: '1rem', borderBottom: '1px solid #dee2e6' }}>
        <Container fluid>
          <div className="d-flex align-items-center justify-content-between">
            <h5 className="mb-0" style={{ fontSize: '1.25rem', fontWeight: 500, color: '#212529' }}>Activities</h5>
            <InputGroup size="sm" style={{ width: '220px' }}>
              <InputGroup.Text style={{ backgroundColor: 'white', borderRight: 'none', fontSize: '0.75rem' }}>
                <Search size={14} />
              </InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search activities"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{ borderLeft: 'none', fontSize: '0.75rem' }}
              />
            </InputGroup>
          </div>
        </Container>
      </div>

      <div className="py-3" style={{ fontSize: '0.75rem' }}>
        <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <div className="d-flex align-items-center">
              <Button
                variant={activityViewMode === 'list' ? 'outline-primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setActivityViewMode('list')}
                style={{
                  width: '40px',
                  height: '34px',
                  padding: 0,
                  borderTopRightRadius: 0,
                  borderBottomRightRadius: 0,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="List view"
              >
                <ListUl size={14} />
              </Button>
              <Button
                variant={activityViewMode === 'calendar' ? 'outline-primary' : 'outline-secondary'}
                size="sm"
                onClick={() => setActivityViewMode('calendar')}
                style={{
                  width: '40px',
                  height: '34px',
                  padding: 0,
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                  marginLeft: '-1px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                title="Calendar view"
              >
                <Calendar3 size={14} />
              </Button>
            </div>
            <Button
              variant="primary"
              size="sm"
              className="d-flex align-items-center"
              onClick={handleOpenScheduleModal}
              style={{ fontSize: '0.75rem', padding: '0.35rem 0.75rem' }}
            >
              <Plus className="me-1" size={14} />
              Activity
              <ChevronDown className="ms-1" size={12} />
            </Button>
            <span className="text-muted" style={{ fontSize: '0.75rem' }}>{filteredMeetings.length} activities</span>
            {/* Inline bulk-action group: appears in the same control row as the
                "+ Activity" button and the count, so it never feels like a
                separate UI surface. Tight grouping (count \u2192 actions \u2192 clear)
                so the eye reads it as one unit. List view only \u2014 calendar
                doesn't render the row checkboxes. */}
            {selectedRowIds.size > 0 && activityViewMode === 'list' && (
              <div
                className="d-flex align-items-center gap-2 ms-2 ps-2"
                style={{ borderLeft: '1px solid #dee2e6', fontSize: '0.75rem' }}
              >
                <span style={{ fontWeight: 500 }}>
                  {selectedRowIds.size} selected
                  {selectedRowIds.size !== selectedFilteredCount && (
                    <span className="text-muted ms-1">
                      ({selectedFilteredCount} visible)
                    </span>
                  )}
                </span>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleBulkSetVisibility('personal')}
                  disabled={isBulkUpdating}
                  style={{ fontSize: '0.7rem', padding: '0.18rem 0.5rem' }}
                >
                  Make Personal
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => handleBulkSetVisibility('global')}
                  disabled={isBulkUpdating}
                  style={{ fontSize: '0.7rem', padding: '0.18rem 0.5rem' }}
                >
                  Make Global
                </Button>
                {isBulkUpdating && <Spinner size="sm" animation="border" />}
                <Button
                  variant="link"
                  size="sm"
                  onClick={handleClearSelection}
                  disabled={isBulkUpdating}
                  style={{ fontSize: '0.7rem', padding: '0.18rem 0.4rem', textDecoration: 'none' }}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <Dropdown
              show={showPriorityFilter}
              onToggle={(nextIsOpen) => setShowPriorityFilter(nextIsOpen)}
            >
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                id="activities-priority-dropdown"
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              >
                {selectedPriority ? getActivityPriorityLabel(selectedPriority) : ALL_PRIORITIES_FILTER}
                <ChevronDown size={12} className="ms-1" />
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ minWidth: '180px', padding: '0.35rem 0' }}>
                <Dropdown.Item
                  onClick={() => {
                    setSelectedPriority('');
                    setShowPriorityFilter(false);
                  }}
                  style={{ fontSize: '0.75rem' }}
                >
                  <div className="d-flex align-items-center">
                    {!selectedPriority && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                    <span>{ALL_PRIORITIES_FILTER}</span>
                  </div>
                </Dropdown.Item>
                <Dropdown.Divider />
                {ACTIVITY_PRIORITY_OPTIONS.map((option) => (
                  <Dropdown.Item
                    key={`priority-filter-${option.value}`}
                    onClick={() => {
                      setSelectedPriority(option.value);
                      setShowPriorityFilter(false);
                    }}
                    style={{ fontSize: '0.75rem' }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      {selectedPriority === option.value && <Check size={14} style={{ color: '#0d6efd' }} />}
                      {selectedPriority !== option.value && <span style={{ width: 14, display: 'inline-block' }} />}
                      <Badge bg={getActivityPriorityVariant(option.value)} style={{ fontSize: '0.65rem' }}>
                        {option.label}
                      </Badge>
                    </div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown
              show={showTagsFilter}
              onToggle={(nextIsOpen) => {
                setShowTagsFilter(nextIsOpen);
                if (!nextIsOpen) setLabelsFilterSearchQuery('');
              }}
            >
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                id="activities-label-dropdown"
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              >
                {selectedTag || ALL_LABELS_FILTER}
                <ChevronDown size={12} className="ms-1" />
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ minWidth: '250px', maxHeight: '400px', overflowY: 'auto', padding: '0.5rem' }}>
                <div className="mb-2">
                  <InputGroup size="sm">
                    <InputGroup.Text>
                      <Search size={14} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search label"
                      value={labelsFilterSearchQuery}
                      onChange={(e) => setLabelsFilterSearchQuery(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      style={{ fontSize: '0.75rem' }}
                    />
                  </InputGroup>
                </div>
                <Dropdown.Item
                  onClick={() => {
                    setSelectedTag('');
                    setShowTagsFilter(false);
                    setLabelsFilterSearchQuery('');
                  }}
                  style={{ fontSize: '0.75rem' }}
                >
                  <div className="d-flex align-items-center">
                    {!selectedTag && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                    <span>{ALL_LABELS_FILTER}</span>
                  </div>
                </Dropdown.Item>
                <Dropdown.Divider />
                {allAvailableLabels
                  .filter((label) => label.toLowerCase().includes(labelsFilterSearchQuery.toLowerCase()))
                  .map((label) => (
                    <Dropdown.Item
                      key={label}
                      onClick={() => {
                        setSelectedTag(label);
                        setShowTagsFilter(false);
                        setLabelsFilterSearchQuery('');
                      }}
                      style={{ fontSize: '0.75rem' }}
                    >
                      <div className="d-flex align-items-center">
                        {selectedTag === label && <Check className="me-2" size={14} style={{ color: '#0d6efd' }} />}
                        <Badge bg={getLabelVariant(label)} style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}>
                          {label}
                        </Badge>
                      </div>
                    </Dropdown.Item>
                  ))}
                <Dropdown.Divider />
                <Dropdown.Item
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCreateFilterLabel();
                  }}
                  style={{ fontSize: '0.75rem', color: '#0d6efd' }}
                >
                  <div className="d-flex align-items-center">
                    <Plus className="me-2" size={14} />
                    <span>Add new label</span>
                  </div>
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown>
            <Dropdown
              show={showOwnerFilter}
              autoClose="outside"
              onToggle={(isOpen) => {
                setShowOwnerFilter(isOpen);
                if (isOpen) {
                  setOwnerFilterSearchQuery('');
                  setOwnerFilterTab('owners');
                  setHoveredOwnerRowKey(null);
                } else {
                  setHoveredOwnerRowKey(null);
                }
              }}
            >
              <Dropdown.Toggle
                variant="outline-secondary"
                size="sm"
                id="activities-owner-dropdown"
                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
              >
                {selectedOwnerButtonLabel}
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ width: '320px', padding: 0, borderRadius: '0.5rem', overflow: 'hidden' }}>
                <div className="p-2 border-bottom" onClick={(event) => event.stopPropagation()}>
                  <InputGroup size="sm">
                    <InputGroup.Text style={{ backgroundColor: '#fff', borderRight: 'none' }}>
                      <Search size={14} />
                    </InputGroup.Text>
                    <Form.Control
                      placeholder="Search owner or filter"
                      value={ownerFilterSearchQuery}
                      onChange={(event) => setOwnerFilterSearchQuery(event.target.value)}
                      style={{ borderLeft: 'none', fontSize: '0.75rem' }}
                    />
                  </InputGroup>
                </div>
                <div className="d-flex border-bottom" onClick={(event) => event.stopPropagation()}>
                  <button
                    type="button"
                    onClick={() => setOwnerFilterTab('favorites')}
                    style={{
                      flex: 1,
                      border: 'none',
                      borderBottom: ownerFilterTab === 'favorites' ? '2px solid #0d6efd' : '2px solid transparent',
                      backgroundColor: 'transparent',
                      color: ownerFilterTab === 'favorites' ? '#0d6efd' : '#6c757d',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '0.5rem 0.25rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.15rem',
                    }}
                  >
                    <Star size={15} />
                    <span>Favorites</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOwnerFilterTab('owners')}
                    style={{
                      flex: 1,
                      border: 'none',
                      borderBottom: ownerFilterTab === 'owners' ? '2px solid #0d6efd' : '2px solid transparent',
                      backgroundColor: 'transparent',
                      color: ownerFilterTab === 'owners' ? '#0d6efd' : '#6c757d',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '0.5rem 0.25rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.15rem',
                    }}
                  >
                    <Person size={15} />
                    <span>Owners</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setOwnerFilterTab('filters')}
                    style={{
                      flex: 1,
                      border: 'none',
                      borderBottom: ownerFilterTab === 'filters' ? '2px solid #0d6efd' : '2px solid transparent',
                      backgroundColor: 'transparent',
                      color: ownerFilterTab === 'filters' ? '#0d6efd' : '#6c757d',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      padding: '0.5rem 0.25rem',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '0.15rem',
                    }}
                  >
                    <Funnel size={15} />
                    <span>Filters</span>
                  </button>
                </div>
                <div style={{ maxHeight: '280px', overflowY: 'auto', overflowX: 'hidden' }} onClick={(event) => event.stopPropagation()}>
                  {ownerFilterTab === 'owners' && (
                    <div className="py-1">
                      {visibleOwnerOptions.length === 0 ? (
                        <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                          No owners found.
                        </div>
                      ) : (
                        visibleOwnerOptions.map((ownerOption) => {
                          const isEveryone = ownerOption.key === normalizeOwnerKey(DEFAULT_OWNER_FILTER_LABEL);
                          const isFavorite = favoriteOwnerKeys.some((entry) => normalizeOwnerKey(entry) === ownerOption.key);
                          const isSelected = normalizeOwnerKey(selectedOwner) === ownerOption.key;
                          const isRowHovered = hoveredOwnerRowKey === ownerOption.key;
                          const rowTextColor = isRowHovered ? '#ffffff' : '#212529';
                          return (
                            <button
                              key={ownerOption.key}
                              type="button"
                              onClick={() => {
                                setSelectedOwner(ownerOption.label);
                                setShowOwnerFilter(false);
                              }}
                              onMouseEnter={() => setHoveredOwnerRowKey(ownerOption.key)}
                              onMouseLeave={() => setHoveredOwnerRowKey((prev) => (prev === ownerOption.key ? null : prev))}
                              style={{
                                width: '100%',
                                border: 'none',
                                backgroundColor: isRowHovered ? '#0d6efd' : 'transparent',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '0.55rem 0.75rem',
                                fontSize: '0.8rem',
                                borderRadius: '0.35rem',
                                boxSizing: 'border-box',
                              }}
                            >
                              <div className="d-flex align-items-center gap-2">
                                <Person size={14} style={{ color: isRowHovered ? '#ffffff' : '#6c757d' }} />
                                <span style={{ fontWeight: isEveryone ? 600 : 500, color: rowTextColor }}>{ownerOption.label}</span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {isEveryone ? (
                                  <span style={{ color: isRowHovered ? '#ffffff' : '#adb5bd', lineHeight: 1, display: 'inline-flex' }}>
                                    <Star size={14} />
                                  </span>
                                ) : (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      toggleFavoriteOwner(ownerOption.key);
                                    }}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        toggleFavoriteOwner(ownerOption.key);
                                      }
                                    }}
                                    style={{
                                      color: isFavorite ? '#f4b400' : (isRowHovered ? '#ffffff' : '#adb5bd'),
                                      lineHeight: 1,
                                      display: 'inline-flex',
                                    }}
                                  >
                                    {isFavorite ? <StarFill size={14} /> : <Star size={14} />}
                                  </span>
                                )}
                                {isSelected ? <Check size={15} style={{ color: isRowHovered ? '#ffffff' : '#111827' }} /> : null}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                  {ownerFilterTab === 'filters' && (
                    <div className="py-1">
                      {visibleVisibilityFilterOptions.length === 0 ? (
                        <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                          No filters found.
                        </div>
                      ) : (
                        visibleVisibilityFilterOptions.map((filterOption) => {
                          const isSelected = selectedVisibilityFilterId === filterOption.id;
                          const isFavorite = favoriteVisibilityFilterIds.includes(filterOption.id);
                          return (
                            <button
                              key={filterOption.id}
                              type="button"
                              onClick={() => setSelectedVisibilityFilterId(filterOption.id)}
                              style={{
                                width: '100%',
                                border: 'none',
                                backgroundColor: 'transparent',
                                textAlign: 'left',
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                                gap: '0.5rem',
                                padding: '0.6rem 0.75rem',
                              }}
                            >
                              <div>
                                <div style={{ fontSize: '0.79rem', fontWeight: 500, color: '#212529' }}>{filterOption.label}</div>
                                <div style={{ fontSize: '0.69rem', color: '#6c757d', marginTop: '0.15rem' }}>
                                  {filterOption.description}
                                </div>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {filterOption.id !== 'all' ? (
                                  <span
                                    role="button"
                                    tabIndex={0}
                                    onClick={(event) => {
                                      event.preventDefault();
                                      event.stopPropagation();
                                      toggleFavoriteVisibilityFilter(filterOption.id);
                                    }}
                                    onKeyDown={(event) => {
                                      if (event.key === 'Enter' || event.key === ' ') {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        toggleFavoriteVisibilityFilter(filterOption.id);
                                      }
                                    }}
                                    style={{ color: isFavorite ? '#f4b400' : '#adb5bd', lineHeight: 1, display: 'inline-flex' }}
                                  >
                                    {isFavorite ? <StarFill size={14} /> : <Star size={14} />}
                                  </span>
                                ) : null}
                                {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                              </div>
                            </button>
                          );
                        })
                      )}
                    </div>
                  )}
                  {ownerFilterTab === 'favorites' && (
                    <div className="py-1">
                      {visibleFavoriteOwnerOptions.length === 0 && visibleFavoriteVisibilityFilterOptions.length === 0 ? (
                        <div className="px-3 py-2 text-muted" style={{ fontSize: '0.75rem' }}>
                          No favorites yet.
                        </div>
                      ) : (
                        <>
                          {visibleFavoriteOwnerOptions.length > 0 ? (
                            <div className="px-3 pt-2 pb-1 text-muted" style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.02em' }}>
                              Owners
                            </div>
                          ) : null}
                          {visibleFavoriteOwnerOptions.map((ownerOption) => {
                            const isSelected = normalizeOwnerKey(selectedOwner) === ownerOption.key;
                            return (
                              <button
                                key={`favorite-owner-${ownerOption.key}`}
                                type="button"
                                onClick={() => {
                                  setSelectedOwner(ownerOption.label);
                                  setShowOwnerFilter(false);
                                }}
                                style={{
                                  width: '100%',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                  textAlign: 'left',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '0.55rem 0.75rem',
                                  fontSize: '0.8rem',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <Person size={14} style={{ color: '#6c757d' }} />
                                  <span style={{ fontWeight: 500, color: '#212529' }}>{ownerOption.label}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <StarFill size={14} style={{ color: '#f4b400' }} />
                                  {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                                </div>
                              </button>
                            );
                          })}
                          {visibleFavoriteVisibilityFilterOptions.length > 0 ? (
                            <div className="px-3 pt-2 pb-1 text-muted" style={{ fontSize: '0.68rem', fontWeight: 600, letterSpacing: '0.02em' }}>
                              Filters
                            </div>
                          ) : null}
                          {visibleFavoriteVisibilityFilterOptions.map((filterOption) => {
                            const isSelected = selectedVisibilityFilterId === filterOption.id;
                            return (
                              <button
                                key={`favorite-filter-${filterOption.id}`}
                                type="button"
                                onClick={() => setSelectedVisibilityFilterId(filterOption.id)}
                                style={{
                                  width: '100%',
                                  border: 'none',
                                  backgroundColor: 'transparent',
                                  textAlign: 'left',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '0.55rem 0.75rem',
                                  fontSize: '0.8rem',
                                }}
                              >
                                <div className="d-flex align-items-center gap-2">
                                  <Funnel size={14} style={{ color: '#6c757d' }} />
                                  <span style={{ fontWeight: 500, color: '#212529' }}>{filterOption.label}</span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <StarFill size={14} style={{ color: '#f4b400' }} />
                                  {isSelected ? <Check size={15} style={{ color: '#111827' }} /> : null}
                                </div>
                              </button>
                            );
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </Dropdown.Menu>
            </Dropdown>
          </div>
        </div>

        <div
          className="d-flex justify-content-between align-items-start flex-wrap gap-2 mb-3"
          style={{ borderBottom: '1px solid #dee2e6', paddingBottom: '0.5rem' }}
        >
          <div className="d-flex flex-wrap gap-2">
            <span className="text-muted me-1" style={{ fontSize: '0.7rem', alignSelf: 'center' }}>Type:</span>
            {['all', 'call', 'meeting', 'email'].map((key) => (
              <Button
                key={key}
                variant={typeFilter === key ? 'primary' : 'outline-secondary'}
                size="sm"
                style={{ fontSize: '0.65rem', padding: '0.18rem 0.42rem' }}
                onClick={() => setTypeFilter(key)}
              >
                {key === 'all' ? 'All' : key.charAt(0).toUpperCase() + key.slice(1)}
              </Button>
            ))}
          </div>
          <div className="d-flex flex-wrap gap-2 ms-auto justify-content-end">
            {[
              { key: 'todo' as DateFilter, label: 'All' },
              { key: 'overdue' as DateFilter, label: 'Overdue' },
              { key: 'today' as DateFilter, label: 'Today' },
              { key: 'tomorrow' as DateFilter, label: 'Tomorrow' },
              { key: 'this-week' as DateFilter, label: 'This week' },
              { key: 'next-week' as DateFilter, label: 'Next week' },
              { key: 'done' as DateFilter, label: 'Done' },
            ].map(({ key, label }) => (
              <Button
                key={key}
                variant={dateFilter === key ? 'primary' : 'outline-secondary'}
                size="sm"
                style={{ fontSize: '0.65rem', padding: '0.18rem 0.42rem' }}
                onClick={() => setDateFilter(key)}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>

        <Card className="crm-table-card">
          <Card.Body className="p-0">
            {isLoadingMeetings ? (
              <div className="text-center py-5 text-muted">Loading activities...</div>
            ) : activityViewMode === 'calendar' ? (
              <div style={{ padding: '0.75rem' }}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleCalendarPreviousMonth}
                    style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
                  >
                    <ChevronLeft size={14} />
                  </Button>
                  <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{calendarMonthLabel}</div>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={handleCalendarNextMonth}
                    style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
                  >
                    <ChevronRight size={14} />
                  </Button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', border: '1px solid #dee2e6', borderBottom: 'none' }}>
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((weekday) => (
                    <div
                      key={weekday}
                      style={{
                        fontSize: '0.68rem',
                        fontWeight: 600,
                        color: '#6c757d',
                        padding: '0.35rem 0.4rem',
                        borderRight: '1px solid #dee2e6',
                        backgroundColor: '#f8f9fa',
                      }}
                    >
                      {weekday}
                    </div>
                  ))}
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, minmax(0, 1fr))', border: '1px solid #dee2e6', borderTop: 'none' }}>
                  {calendarDays.map((day, index) => {
                    const dayKey = day ? getDateKeyInTimezone(day.toISOString(), tenantTimezone) : '';
                    const dayActivities = dayKey ? (calendarActivitiesByDate.get(dayKey) || []) : [];
                    const isTodayCell = Boolean(dayKey && dayKey === todayCalendarKey);
                    const isLastColumn = index % 7 === 6;
                    return (
                      <div
                        key={day ? `${day.toISOString()}-${index}` : `blank-${index}`}
                        style={{
                          minHeight: '94px',
                          padding: '0.3rem 0.35rem',
                          borderRight: isLastColumn ? 'none' : '1px solid #dee2e6',
                          borderBottom: '1px solid #dee2e6',
                          backgroundColor: day ? (isTodayCell ? '#f3f8ff' : '#fff') : '#fafafa',
                        }}
                      >
                        {day && (
                          <>
                            <div
                              style={{
                                fontSize: '0.68rem',
                                fontWeight: 600,
                                color: isTodayCell ? '#0d6efd' : '#495057',
                                marginBottom: '0.2rem',
                              }}
                            >
                              {day.getDate()}
                            </div>
                            <div className="d-flex flex-column gap-1">
                              {dayActivities.slice(0, 3).map((meeting) => (
                                <div
                                  key={meeting.id}
                                  role="button"
                                  tabIndex={0}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpenActivityDetail(meeting);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      handleOpenActivityDetail(meeting);
                                    }
                                  }}
                                  style={{
                                    fontSize: '0.62rem',
                                    lineHeight: 1.2,
                                    color: '#1f2937',
                                    backgroundColor: '#eef5ff',
                                    borderRadius: 4,
                                    padding: '0.15rem 0.25rem',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    cursor: 'pointer',
                                  }}
                                  title={`${toLocaleTimeStringInTimezone(
                                    meeting.startTime,
                                    'en-US',
                                    { hour: 'numeric', minute: '2-digit' },
                                    tenantTimezone
                                  )} ${meeting.title || getTypeLabel(meeting)}`}
                                >
                                  {toLocaleTimeStringInTimezone(
                                    meeting.startTime,
                                    'en-US',
                                    { hour: 'numeric', minute: '2-digit' },
                                    tenantTimezone
                                  )}{' '}
                                  {meeting.title || getTypeLabel(meeting)}
                                </div>
                              ))}
                              {dayActivities.length > 3 && (
                                <div style={{ fontSize: '0.6rem', color: '#6c757d' }}>
                                  +{dayActivities.length - 3} more
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <Table responsive hover className="mb-0 crm-table activities-table" style={{ fontSize: '0.75rem' }}>
                <colgroup>
                  {/* Order: Select | Due date | Organization | Contact person | Subject | Priority | Labels | Done.
                      Column 1 is the bulk-select checkbox (select-all in the
                      header, per-row in the body). Done checkbox sits at the
                      end as a per-row action. Subject is the flex column so it
                      absorbs spare horizontal space. */}
                  <col style={{ width: '40px' }} />
                  <col style={{ width: '130px' }} />
                  <col style={{ width: '220px' }} />
                  <col style={{ width: '180px' }} />
                  <col />
                  <col style={{ width: '96px' }} />
                  <col style={{ width: '140px', minWidth: '110px' }} />
                  <col style={{ width: '60px' }} />
                </colgroup>
                <thead className="table-light">
                  <tr>
                    <th style={{ width: '40px', fontSize: '0.75rem', textAlign: 'center' }}>
                      <Form.Check
                        type="checkbox"
                        checked={headerCheckboxState === 'all'}
                        onChange={handleToggleSelectAll}
                        // Indeterminate is a DOM-only property (no HTML attribute),
                        // so we set it via a ref callback whenever the derived
                        // state says "some rows selected".
                        ref={(el: HTMLInputElement | null) => {
                          if (el) el.indeterminate = headerCheckboxState === 'some';
                        }}
                        aria-label="Select all visible activities"
                      />
                    </th>
                    <th style={{ width: '130px', fontSize: '0.75rem', whiteSpace: 'nowrap' }}>Due date</th>
                    <th style={{ width: '220px', fontSize: '0.75rem' }}>Organization</th>
                    <th style={{ width: '180px', fontSize: '0.75rem' }}>Contact person</th>
                    <th style={{ fontSize: '0.75rem' }}>Subject</th>
                    <th style={{ width: '96px', fontSize: '0.75rem' }}>Priority</th>
                    <th style={{ width: '140px', fontSize: '0.75rem' }}>Labels</th>
                    <th style={{ width: '60px', fontSize: '0.75rem' }}>Done</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMeetings.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-5 text-muted">
                        No activities match your filters. Create activities from Clients, Contacts, Leads, Deals, or Meetings.
                      </td>
                    </tr>
                  ) : (
                    filteredMeetings.map((meeting) => {
                      const rowLabels = getActivityVisibleLabels(meeting);
                      const isRowSelected = selectedRowIds.has(meeting.id);
                      return (
                      <tr
                        key={meeting.id}
                        style={{
                          cursor: 'pointer',
                          // Subtle highlight on selected rows so operators can
                          // see what they've ticked even when scrolled past the
                          // bulk-action bar.
                          backgroundColor: isRowSelected ? 'rgba(13, 110, 253, 0.06)' : undefined,
                        }}
                      >
                        <td
                          onClick={(event) => event.stopPropagation()}
                          style={{ textAlign: 'center' }}
                        >
                          <Form.Check
                            type="checkbox"
                            checked={isRowSelected}
                            onChange={() => handleToggleRowSelected(meeting.id)}
                            aria-label={isRowSelected ? 'Deselect activity' : 'Select activity'}
                          />
                        </td>
                        {(() => {
                          // Highlight overdue activities so operators see them at a glance.
                          // Same overdue logic as the date filter (key < todayKey, in tenant
                          // timezone). Done items don't shout red \u2014 they're already handled.
                          const rowDateKey = meeting.startTime
                            ? getDateKeyInTimezone(meeting.startTime, tenantTimezone)
                            : '';
                          const isDone = getMeetingDoneValue(meeting);
                          const isOverdue = !isDone && Boolean(rowDateKey) && rowDateKey < todayKey;
                          return (
                            <td
                              onClick={() => handleOpenActivityDetail(meeting)}
                              className={isOverdue ? 'text-danger' : undefined}
                              style={{
                                textDecoration: isDone ? 'line-through' : undefined,
                                opacity: isDone ? 0.6 : 1,
                                fontWeight: isOverdue ? 600 : undefined,
                              }}
                            >
                              <div className="d-flex align-items-center text-nowrap">
                                <Clock size={14} className={`me-1 ${isOverdue ? 'text-danger' : 'text-muted'}`} />
                                <span>
                                  {meeting.startTime
                                    ? toLocaleDateStringInTimezone(
                                      meeting.startTime,
                                      'en-US',
                                      { month: 'short', day: 'numeric', year: 'numeric' },
                                      tenantTimezone
                                    )
                                    : '\u2014'}
                                </span>
                              </div>
                            </td>
                          );
                        })()}
                        <td
                          onClick={() => handleOpenActivityDetail(meeting)}
                          style={{
                            whiteSpace: 'nowrap',
                            textDecoration: getMeetingDoneValue(meeting) ? 'line-through' : undefined,
                            opacity: getMeetingDoneValue(meeting) ? 0.6 : 1,
                          }}
                        >
                          <div className="d-flex align-items-center text-nowrap">
                            <Building size={14} className="me-1 text-muted" />
                            {getOrganization(meeting)}
                          </div>
                        </td>
                        <td
                          onClick={() => handleOpenActivityDetail(meeting)}
                          style={{
                            textDecoration: getMeetingDoneValue(meeting) ? 'line-through' : undefined,
                            opacity: getMeetingDoneValue(meeting) ? 0.6 : 1,
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <Person size={14} className="me-1 text-muted" />
                            {getContactPerson(meeting)}
                          </div>
                        </td>
                        <td
                          onClick={() => handleOpenActivityDetail(meeting)}
                          style={{
                            textDecoration: getMeetingDoneValue(meeting) ? 'line-through' : undefined,
                            opacity: getMeetingDoneValue(meeting) ? 0.6 : 1,
                          }}
                        >
                          <div className="d-flex align-items-center">
                            <TypeIcon meeting={meeting} />
                            {meeting.title || getTypeLabel(meeting)}
                          </div>
                        </td>
                        <td
                          onClick={() => handleOpenActivityDetail(meeting)}
                          style={{
                            textDecoration: getMeetingDoneValue(meeting) ? 'line-through' : undefined,
                            opacity: getMeetingDoneValue(meeting) ? 0.6 : 1,
                          }}
                        >
                          <Badge bg={getActivityPriorityVariant(getMeetingPriorityValue(meeting))} style={{ fontSize: '0.68rem' }}>
                            {getActivityPriorityLabel(getMeetingPriorityValue(meeting))}
                          </Badge>
                        </td>
                        <td
                          onClick={() => handleOpenActivityDetail(meeting)}
                          style={{
                            textDecoration: getMeetingDoneValue(meeting) ? 'line-through' : undefined,
                            opacity: getMeetingDoneValue(meeting) ? 0.6 : 1,
                            verticalAlign: 'middle',
                          }}
                        >
                          <div className="d-flex flex-wrap align-items-center gap-1">
                            {rowLabels.length === 0 ? (
                              <span className="text-muted">—</span>
                            ) : (
                              rowLabels.map((label) => (
                                <Badge
                                  key={`${meeting.id}-row-label-${label}`}
                                  bg={getLabelVariant(label)}
                                  style={{ fontSize: '0.6rem', padding: '0.15rem 0.4rem' }}
                                >
                                  {label}
                                </Badge>
                              ))
                            )}
                          </div>
                        </td>
                        <td
                          onClick={(event) => {
                            event.stopPropagation();
                          }}
                        >
                          <Form.Check
                            type="checkbox"
                            checked={getMeetingDoneValue(meeting)}
                            onChange={(event) => handleToggleMeetingDone(meeting, event.target.checked)}
                            aria-label={getMeetingDoneValue(meeting) ? 'Mark activity not done' : 'Mark activity done'}
                          />
                        </td>
                      </tr>
                      );
                    })
                  )}
                </tbody>
              </Table>
            )}
          </Card.Body>
        </Card>
      </div>

      <Modal show={showScheduleModal} onHide={handleCloseScheduleModal} size="lg">
        <Modal.Header style={{ fontSize: '1rem', padding: '0.75rem 1rem', borderBottom: '1px solid #dee2e6' }}>
          <Modal.Title style={{ fontSize: '1.1rem', fontWeight: 500 }}>
            {editingMeetingId ? 'Edit activity' : 'Schedule an activity'}
          </Modal.Title>
          <Button variant="link" onClick={handleCloseScheduleModal} style={{ padding: 0, fontSize: '18px', color: '#6c757d' }}>
            <X />
          </Button>
        </Modal.Header>
        <Modal.Body style={{ fontSize: '0.75rem', padding: '1rem' }}>
          {renderActivityEditor()}
        </Modal.Body>
        <Modal.Footer style={{ fontSize: '0.75rem', padding: '0.75rem 1rem', borderTop: '1px solid #dee2e6' }}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <Form.Check
              type="checkbox"
              id="markAsDone"
              label="Mark as done"
              checked={activityForm.markAsDone}
              onChange={(e) => handleActivityFieldChange('markAsDone', e.target.checked)}
              style={{ fontSize: '0.75rem', marginBottom: 0 }}
            />
            <div className="d-flex gap-2">
              <Button variant="secondary" size="sm" onClick={handleCloseScheduleModal}>
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={handleSaveActivity}
                disabled={
                  isCreatingMeeting
                  || isUpdatingMeeting
                  || !activityForm.title
                  || !activityForm.startTime
                  || !activityForm.endTime
                }
              >
                {isCreatingMeeting || isUpdatingMeeting ? <Spinner size="sm" animation="border" /> : 'Save'}
              </Button>
            </div>
          </div>
        </Modal.Footer>
      </Modal>

      <Offcanvas
        show={showActivitySidebar}
        onHide={handleCloseActivitySidebar}
        placement="end"
        style={{
          width: 'calc(100vw - 510px)',
          maxWidth: '1200px',
          minWidth: '800px',
          top: '56px',
          height: 'calc(100vh - 56px)',
          zIndex: 1040,
        }}
      >
        <Offcanvas.Header
          className="position-relative"
          style={{
            borderBottom: '1px solid #dee2e6',
            paddingTop: '0.95rem',
            paddingBottom: '1.05rem',
          }}
        >
          <Offcanvas.Title style={{ flex: 1 }}>
            {selectedMeeting && (
              <div>
                <div className="fw-bold" style={{ fontSize: '0.85rem' }}>
                  {selectedMeeting.title || getTypeLabel(selectedMeeting)}
                </div>
                <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
                  {getOrganization(selectedMeeting) !== '—' ? getOrganization(selectedMeeting) : getTypeLabel(selectedMeeting)}
                </div>
              </div>
            )}
          </Offcanvas.Title>
          <Button
            variant="link"
            className="p-0 position-absolute"
            onClick={handleCloseActivitySidebar}
            style={{ top: '1.4rem', right: '1rem', zIndex: 10 }}
            aria-label="Close activity"
          >
            <X size={20} />
          </Button>
        </Offcanvas.Header>
        <Offcanvas.Body className="p-0">
          {selectedMeeting && (
            <div className="d-flex h-100" style={{ overflowX: 'hidden' }}>
              <div
                className="crm-sidebar-left-column"
                style={{
                  width: '320px',
                  padding: '1rem',
                  borderRight: '1px solid #dee2e6',
                  overflowY: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <div className="mb-4" style={{ flex: 1 }}>
                  <div className="mb-3">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>DETAILS</h6>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => setShowAllActivityDetailFields((prev) => !prev)}
                      >
                        {showAllActivityDetailFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>
                    {showAllActivityDetailFields && (
                      <div className="mb-2 d-flex align-items-center gap-2" style={{ padding: '0.5rem', borderRadius: '4px' }}>
                        <span style={activitySidebarIconStyle}>
                          <FileText size={14} />
                        </span>
                        <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>Add to sequence</span>
                      </div>
                    )}
                    {renderActivityLabelsRow(showAllActivityDetailFields)}
                    {renderActivitySidebarField(
                      'priority',
                      'Priority',
                      Flag,
                      { showAll: true }
                    )}
                    {renderActivitySidebarField(
                      'value',
                      '$ Value',
                      CurrencyDollar,
                      { showAll: showAllActivityDetailFields }
                    )}
                    {renderActivitySidebarField(
                      'expectedCloseDate',
                      'Expected close date',
                      Calendar,
                      { showAll: showAllActivityDetailFields }
                    )}
                    {renderActivitySidebarField(
                      'businessUnit',
                      'Business Unit:',
                      Briefcase,
                      { showAll: showAllActivityDetailFields }
                    )}
                    {renderActivitySidebarField(
                      'owner',
                      'Owner',
                      Person,
                      { showAll: true }
                    )}
                    {renderActivitySidebarField(
                      'visibility',
                      'Visibility',
                      PeopleFill,
                      { showAll: true }
                    )}
                    {renderActivitySidebarField(
                      'sourceOrigin',
                      'Source origin',
                      ArrowRight,
                      { showAll: showAllActivityDetailFields, showInfo: true }
                    )}
                    {renderActivitySidebarField(
                      'sourceChannel',
                      'Source channel',
                      ArrowRight,
                      { showAll: showAllActivityDetailFields }
                    )}
                    {renderActivitySidebarField(
                      'sourceChannelId',
                      'Source channel ID',
                      Dot,
                      { showAll: showAllActivityDetailFields }
                    )}
                  </div>

                  <div className="mb-3" style={{ marginTop: '2rem' }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>PERSON</h6>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => setShowAllActivityPersonFields((prev) => !prev)}
                      >
                        {showAllActivityPersonFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>
                    {renderActivitySidebarField(
                      'name',
                      'No contact',
                      Person,
                      { showAll: showAllActivityPersonFields }
                    )}
                    {renderActivitySidebarField(
                      'title',
                      'Title',
                      FileText,
                      { showAll: showAllActivityPersonFields }
                    )}
                    {selectedMeeting && (() => {
                      const personTarget = getActivityPersonTarget();
                      const personRecord: any = personTarget?.record || {};
                      const decisionMakerValue = Boolean(personRecord.decisionMaker);
                      return (
                        <div className="d-flex align-items-center gap-2 mb-2" style={{ padding: '0.5rem', borderRadius: '4px' }}>
                          <span style={activitySidebarIconStyle}>
                            {decisionMakerValue
                              ? <DiamondFill size={14} style={{ color: '#0d6efd' }} />
                              : <Diamond size={14} />}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#212529', flex: 1 }}>Decision Maker</span>
                          <Form.Check
                            type="switch"
                            id={`activity-decision-maker-switch-${selectedMeeting.id}`}
                            checked={decisionMakerValue}
                            disabled={!personTarget}
                            onChange={async (e) => {
                              const next = e.target.checked;
                              if (decisionMakerValue === next) return;
                              if (!personTarget) return;
                              try {
                                await saveActivityEntityUpdate(personTarget, { decisionMaker: next });
                              } catch (error: any) {
                                console.error('Error updating decisionMaker:', error);
                                alert(`Failed to update Decision Maker: ${error?.data?.message || error?.message || 'Unknown error'}`);
                              }
                            }}
                            style={{ fontSize: '0.75rem' }}
                          />
                        </div>
                      );
                    })()}
                    {renderActivitySidebarField(
                      'contactStage',
                      'Contact Stage',
                      CheckSquare,
                      { showAll: showAllActivityPersonFields }
                    )}
                    {renderActivitySidebarField(
                      'email',
                      'No email',
                      Envelope,
                      { showAll: showAllActivityPersonFields }
                    )}
                    {renderActivitySidebarField(
                      'phone',
                      'No phone',
                      Phone,
                      { showAll: showAllActivityPersonFields }
                    )}
                    {renderActivitySidebarField(
                      'workPhone',
                      'Work phone',
                      Phone,
                      { showAll: showAllActivityPersonFields }
                    )}
                    {renderActivitySidebarField(
                      'cellPhone',
                      'Cell phone',
                      Phone,
                      { showAll: showAllActivityPersonFields }
                    )}
                    {renderActivitySidebarField(
                      'assistantName',
                      "Assistant's Name",
                      Person,
                      { showAll: showAllActivityPersonFields }
                    )}
                    {renderActivitySidebarField(
                      'assistantPhone',
                      "Assistant's Phone",
                      Phone,
                      { showAll: showAllActivityPersonFields }
                    )}
                    {renderActivitySidebarField(
                      'assistantEmail',
                      "Assistant's Email",
                      Envelope,
                      { showAll: showAllActivityPersonFields }
                    )}
                  </div>

                  <div className="mb-3" style={{ marginTop: '2rem' }}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <h6 className="text-uppercase text-muted small mb-0" style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>ORGANIZATION</h6>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-0"
                        style={{ fontSize: '0.75rem' }}
                        onClick={() => setShowAllActivityOrganizationFields((prev) => !prev)}
                      >
                        {showAllActivityOrganizationFields ? 'Hide fields' : 'Show all fields'}
                      </Button>
                    </div>
                    {renderActivitySidebarField(
                      'organization',
                      'No organization',
                      BuildingFill,
                      { showAll: showAllActivityOrganizationFields }
                    )}
                    {(() => {
                      if (!selectedMeeting) return null;
                      const accountId = String(selectedMeeting.accountId || '').trim();
                      if (!accountId) return null;
                      const account = accountMap.get(accountId) as any;
                      const displayDate = String(account?.contractRenewalDate || '').trim();
                      const hasValue = Boolean(displayDate);

                      return (
                        <div
                          className="mb-2"
                          style={{
                            cursor: 'pointer',
                            padding: '0.5rem',
                            borderRadius: '4px',
                            backgroundColor: activitySidebarHoveredField === 'organization' && !isEditingContractRenewal ? '#f8f9fa' : 'transparent',
                            transition: 'background-color 0.2s'
                          }}
                          onMouseEnter={() => setActivitySidebarHoveredField('organization')}
                          onMouseLeave={() => setActivitySidebarHoveredField(null)}
                          onClick={() => {
                            setContractRenewalDraft(displayDate);
                            setIsEditingContractRenewal(true);
                          }}
                        >
                          <div className="d-flex align-items-center gap-2">
                            <span style={activitySidebarIconStyle}>
                              <Calendar size={14} />
                            </span>
                            <span style={{ fontSize: '0.75rem', color: hasValue ? '#212529' : '#6c757d' }}>
                              {hasValue ? displayDate : '+ Add Contract Renewal'}
                            </span>
                          </div>
                          {isEditingContractRenewal && (
                            <div className="d-flex align-items-center gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                              <Form.Control
                                type="date"
                                value={contractRenewalDraft}
                                onChange={(e) => setContractRenewalDraft(e.target.value)}
                                style={{ fontSize: '0.75rem', padding: '0.25rem 0.5rem', flex: 1 }}
                                autoFocus
                              />
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={async () => {
                                  try {
                                    const nextDate = String(contractRenewalDraft || '').trim();
                                    await updateAccount({ accountId, contractRenewalDate: nextDate || '' }).unwrap();
                                    setIsEditingContractRenewal(false);
                                    refetchAccounts();
                                  } catch (error: any) {
                                    console.error('Error updating contract renewal:', error);
                                    alert(`Failed to update Contract Renewal: ${error?.data?.message || error?.message || 'Unknown error'}`);
                                  }
                                }}
                              >
                                Save
                              </Button>
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={() => setIsEditingContractRenewal(false)}
                              >
                                Cancel
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {renderActivitySidebarField(
                      'accountStage',
                      'Account Stage',
                      Briefcase,
                      { showAll: showAllActivityOrganizationFields }
                    )}
                    {renderActivitySidebarField(
                      'address',
                      'Address',
                      GeoAltFill,
                      { showAll: showAllActivityOrganizationFields }
                    )}
                    {renderActivitySidebarField(
                      'website',
                      'Website',
                      Globe2,
                      { showAll: showAllActivityOrganizationFields }
                    )}
                    {renderActivitySidebarField(
                      'linkedInProfile',
                      'LinkedIn profile',
                      Linkedin,
                      { showAll: showAllActivityOrganizationFields }
                    )}
                    {renderActivitySidebarField(
                      'numberOfEmployees',
                      'Number of employees',
                      PeopleFill,
                      { showAll: showAllActivityOrganizationFields }
                    )}
                    {renderActivitySidebarField(
                      'annualRevenue',
                      'Annual revenue',
                      CurrencyDollar,
                      { showAll: showAllActivityOrganizationFields }
                    )}
                    {renderActivitySidebarField(
                      'industry',
                      'Industry',
                      Briefcase,
                      { showAll: showAllActivityOrganizationFields }
                    )}
                  </div>
                </div>

                <div style={{ marginTop: 'auto', paddingTop: '1rem', borderTop: '1px solid #dee2e6' }}>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleDeleteActivityFromSidebar}
                      disabled={isDeletingMeetings}
                      style={{
                        width: '32px',
                        height: '32px',
                        padding: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderColor: '#ced4da',
                      }}
                      title="Delete activity"
                    >
                      {isDeletingMeetings ? <Spinner size="sm" animation="border" /> : <Trash size={14} />}
                    </Button>
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={handleEditActivityFromSidebar}
                      style={{ fontSize: '0.7rem', padding: '0.35rem 0.75rem', width: 'auto', borderRadius: '10px' }}
                    >
                      Edit activity
                    </Button>
                  </div>
                </div>
              </div>

              <div style={{ flex: 1, padding: '1rem', overflowY: 'auto', overflowX: 'hidden' }}>
                <Tabs
                  activeKey={activitySidebarTab}
                  onSelect={(k) => k && setActivitySidebarTab(k as 'notes' | 'activity' | 'email' | 'files')}
                  className="mb-3"
                  style={{ fontSize: '0.75rem' }}
                >
                  <Tab eventKey="notes" title="Notes">
                    <div
                      className="mb-3"
                      style={{
                        backgroundColor: '#ffffff',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                      }}
                    >
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={activitySidebarNoteDraft}
                        onChange={(e) => setActivitySidebarNoteDraft(e.target.value)}
                        placeholder="Take a note, @name..."
                        style={{
                          border: 'none',
                          fontSize: '0.75rem',
                          minHeight: '100px',
                          boxShadow: 'none',
                        }}
                      />
                      <div
                        className="d-flex justify-content-end gap-2 border-top px-2 py-2"
                        style={{ backgroundColor: '#f8f9fa' }}
                      >
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => setActivitySidebarNoteDraft(String(selectedMeeting?.notes || ''))}
                          disabled={!isSidebarNoteDirty || isUpdatingMeeting}
                          style={{ fontSize: '0.7rem' }}
                        >
                          Reset
                        </Button>
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={handleSaveSidebarNotes}
                          disabled={!isSidebarNoteDirty || isUpdatingMeeting}
                          style={{ fontSize: '0.7rem' }}
                        >
                          {isUpdatingMeeting ? <Spinner size="sm" animation="border" /> : 'Save note'}
                        </Button>
                      </div>
                    </div>

                    <div className="mb-3">
                      <h6 className="small mb-2" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>Focus</h6>
                      <div className="d-flex align-items-start gap-2 mb-2">
                        <div
                          className="d-inline-flex align-items-center justify-content-center border"
                          style={{
                            width: '32px',
                            height: '32px',
                            borderRadius: '999px',
                            backgroundColor: '#f8f9fa',
                            color: '#495057',
                            marginTop: '2px',
                          }}
                        >
                          <TypeIcon meeting={selectedMeeting} />
                        </div>
                        <div className="flex-grow-1 border rounded" style={{ backgroundColor: '#ffffff', padding: '0.5rem 0.65rem' }}>
                          <div className="fw-semibold" style={{ fontSize: '0.75rem' }}>
                            {selectedMeeting.title || getTypeLabel(selectedMeeting)}
                          </div>
                          <div className="d-flex flex-wrap align-items-center gap-2 text-muted mt-1" style={{ fontSize: '0.7rem' }}>
                            <span>{getMeetingDateTimeLabel(selectedMeeting.startTime)}</span>
                            <span className="d-inline-flex align-items-center gap-1">
                              <Person size={11} />
                              {getContactPerson(selectedMeeting)}
                            </span>
                          </div>
                        </div>
                      </div>
                      <p className="text-muted small mb-2" style={{ fontSize: '0.7rem' }}>
                        Scheduled activities, pinned notes, email drafts and scheduled emails will appear here.
                      </p>
                      <div className="d-flex flex-wrap gap-2">
                        {['In 1h', 'In 3h', 'Tomorrow', 'Next week', '+ Other'].map((label) => (
                          <Button key={label} variant="outline-secondary" size="sm" style={{ fontSize: '0.7rem' }} disabled>
                            {label}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="mt-3">
                      <h6 className="small mb-2" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>History</h6>
                      <div className="text-muted small" style={{ fontSize: '0.75rem' }}>
                        Activity created
                      </div>
                      <div style={{ fontSize: '1.05rem', fontWeight: 300, lineHeight: 1.2 }}>
                        {getMeetingDateTimeLabel(selectedMeeting.createdAt || selectedMeeting.startTime)} · PhiLL Kross
                      </div>
                    </div>
                  </Tab>

                  <Tab eventKey="activity" title="Activity">
                    <div className="py-2" style={{ fontSize: '0.75rem' }}>
                      {renderActivityEditor(true)}
                    </div>
                  </Tab>

                  <Tab eventKey="email" title="Email">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <h6 className="small mb-0" style={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                        Linked threads
                      </h6>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => refetchActivityEmailThreads()}
                        disabled={!activityEmailAddress || isRefreshingActivityEmailThreads}
                        style={{ fontSize: '0.65rem', padding: '0.2rem 0.5rem' }}
                      >
                        {isRefreshingActivityEmailThreads ? <Spinner size="sm" animation="border" /> : 'Refresh'}
                      </Button>
                    </div>
                    {!activityEmailAddress ? (
                      <p className="text-muted small" style={{ fontSize: '0.7rem' }}>
                        Add an email address (via linked contact/lead) to this activity to link Unibox threads.
                      </p>
                    ) : isLoadingActivityEmailThreads ? (
                      <div className="d-flex align-items-center gap-2 text-muted small" style={{ fontSize: '0.7rem' }}>
                        <Spinner size="sm" animation="border" />
                        Loading email threads...
                      </div>
                    ) : activityEmailThreadsError ? (
                      <Alert variant="danger" className="py-2 px-3 mb-0" style={{ fontSize: '0.7rem' }}>
                        Failed to load email threads.
                      </Alert>
                    ) : activityEmailThreads.length === 0 ? (
                      <p className="text-muted small mb-0" style={{ fontSize: '0.7rem' }}>
                        No email threads found for {activityEmailAddress}.
                      </p>
                    ) : (
                      <ListGroup variant="flush">
                        {activityEmailThreads.map((thread) => (
                          <ListGroup.Item key={thread.id} className="px-0 py-2" style={{ fontSize: '0.75rem' }}>
                            <div className="d-flex align-items-start gap-2">
                              <Envelope size={14} className="mt-1" style={{ color: '#6c757d' }} />
                              <div className="flex-grow-1">
                                <div className="d-flex align-items-start justify-content-between gap-2">
                                  <div className="fw-semibold" style={{ fontSize: '0.75rem' }}>
                                    {thread.subject}
                                  </div>
                                  <div className="d-flex align-items-center gap-1">
                                    {thread.unreadCount > 0 && (
                                      <Badge bg="primary" style={{ fontSize: '0.6rem' }}>
                                        {thread.unreadCount} unread
                                      </Badge>
                                    )}
                                    {thread.messageCount > 1 && (
                                      <Badge bg="secondary" style={{ fontSize: '0.6rem' }}>
                                        {thread.messageCount} msgs
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <div className="text-muted small mt-1" style={{ fontSize: '0.7rem' }}>
                                  {formatDate(thread.latestDate)} · {thread.latestFrom || 'Unknown sender'}
                                </div>
                                <div className="mt-2 d-flex flex-column gap-2">
                                  {thread.messages.map((message) => (
                                    <div key={`${thread.id}-${message.id}`} className="border-start ps-2">
                                      <div className="text-muted small" style={{ fontSize: '0.67rem' }}>
                                        {formatDate(message.date)} · {message.from || 'Unknown sender'}
                                      </div>
                                      <div className="small mt-1" style={{ fontSize: '0.7rem', lineHeight: 1.35 }}>
                                        {message.preview || 'No preview available.'}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    )}
                  </Tab>

                  <Tab eventKey="files" title="Files">
                    <div className="text-muted" style={{ fontSize: '0.75rem' }}>
                      No files attached.
                    </div>
                  </Tab>
                </Tabs>
              </div>
            </div>
          )}
        </Offcanvas.Body>
      </Offcanvas>
    </Container>
  );
};

export default Activities;
